import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { useRequireMfa } from '@/hooks/use-require-mfa';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LogoShield } from '@/components/LogoShield';
import { Helmet } from '@/components/Helmet';
import { toast } from 'sonner';
import { formatEdgeFunctionError } from '@/lib/edge-function-error';
import { invokeEdgeWithAuthRetry } from '@/lib/invoke-edge-auth';
import { ArrowLeft, Globe, Loader2, Mail, Shield, User, RefreshCw, Trash2, RotateCcw, Download, Link2, ShieldCheck, AlertTriangle, ExternalLink, Activity, MoreHorizontal } from 'lucide-react';
import { CurrencyDock } from '@/components/portal/CurrencyDock';
import { AuthenticatedHeader } from '@/components/portal/AuthenticatedHeader';
import { AdminAiAssistantWidget } from '@/components/portal/AdminAiAssistantWidget';
import { AdminMfaOutreachCard } from '@/components/portal/AdminMfaOutreachCard';
import { resolveEffectiveSubscription } from '@/lib/subscription-resolver';
import { RoleManagementCard } from '@/components/portal/RoleManagementCard';
import { STATUS_LABELS } from '@/lib/vocabulary/securityTerminology';
import { AltchaWidget } from '@/components/AltchaWidget';
import {
  PORTFOLIO_PRICING_STATUS_LABELS,
  getPortfolioPricingStatusTone,
} from '@/lib/portfolioPricing';
import {
  buildPortfolioPricingAnalyticsProps,
  capturePortfolioPricingEvent,
} from '@/lib/portfolioPricingAnalytics';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  resolveHostedAutomation,
  resolveHostedPluginInstall,
  SHADOWSCAN_WORDPRESS_PLUGIN,
} from '@/lib/wordpressLifecycle';

const getPairingCode = (pairingLink?: string) => {
  if (!pairingLink) return null;
  try {
    const url = new URL(pairingLink);
    return url.searchParams.get('pairing_code');
  } catch {
    const match = pairingLink.match(/pairing_code=([^&]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }
};

const normalizeDomainValue = (value?: string | null) => {
  if (!value) return null;
  const candidate = value.trim();
  if (!candidate) return null;
  try {
    const url = new URL(candidate.includes('://') ? candidate : `https://${candidate}`);
    return url.hostname.replace(/^www\./, '').toLowerCase();
  } catch {
    return candidate.replace(/^https?:\/\//, '').replace(/^www\./, '').toLowerCase();
  }
};

const DISCONNECTED_TTL_MS = 24 * 60 * 60 * 1000;

const getHeartbeatSummary = (timestamp?: string | null) => {
  if (!timestamp) return { label: 'Never', variant: 'danger' as const };
  const lastMs = Date.parse(timestamp);
  if (!Number.isFinite(lastMs)) return { label: 'Unknown', variant: 'neutral' as const };
  const ageMs = Date.now() - lastMs;
  if (ageMs <= 15 * 60 * 1000) return { label: 'Healthy', variant: 'success' as const };
  return { label: 'Stale', variant: 'warning' as const };
};

const formatTimestamp = (value?: string | null) => {
  if (!value) return 'Unknown';
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return 'Unknown';
  return new Date(parsed).toLocaleString();
};

const isHeartbeatFresh = (timestamp?: string | null) => {
  if (!timestamp) return false;
  const lastMs = Date.parse(timestamp);
  if (!Number.isFinite(lastMs)) return false;
  return Date.now() - lastMs <= DISCONNECTED_TTL_MS;
};

const parseConnectorReport = (report?: string | null) => {
  if (!report) return {} as Record<string, string>;
  return report.split('\n').reduce<Record<string, string>>((acc, line) => {
    const idx = line.indexOf(':');
    if (idx <= 0) return acc;
    const key = line.slice(0, idx).trim();
    const value = line.slice(idx + 1).trim();
    if (key) acc[key] = value;
    return acc;
  }, {});
};

type PostureScoreTone = 'success' | 'warning' | 'danger' | 'neutral';

const getScoreTone = (score?: number | null): PostureScoreTone => {
  if (typeof score !== 'number') return 'neutral';
  if (score >= 80) return 'success';
  if (score >= 50) return 'warning';
  return 'danger';
};

const formatBreakdownLabel = (key: string) => {
  const labels: Record<string, string> = {
    heartbeat_connected: 'Heartbeat connected',
    logs_pipeline: 'Security activity pipeline',
    owasp_reporting: 'Protection checks reporting',
    connector_presence: 'Connector installed',
    mfa_coverage: 'MFA coverage',
    plugin_auto_updates: 'Plugin auto-updates',
    password_policy: 'Password policy (change/reset)',
    geo_policy: 'Admin access location rules',
    vuln_penalty: 'Known vulnerabilities',
    config_penalty: 'Risky configuration',
  };
  return labels[key] || key.replace(/_/g, ' ');
};

const getTopDrivers = (breakdown?: Record<string, number> | null, limit = 2) => {
  if (!breakdown) return [];
  return Object.entries(breakdown)
    .map(([key, value]) => ({ key, value }))
    .sort((a, b) => a.value - b.value)
    .slice(0, limit)
    .map((entry) => ({
      label: formatBreakdownLabel(entry.key),
      value: entry.value,
    }));
};

interface AdminDomain {
  id: string;
  domainName: string;
  wpAdminUrl?: string | null;
  status: string | null;
  createdAt: string | null;
  tier: string | null;
  stripePriceId: string | null;
  stripeSubscriptionId?: string | null;
  promoPrice?: number | null;
}

interface DeletedDomain {
  id: string;
  domainName: string;
  status: string | null;
  deletedAt: string | null;
  createdAt: string | null;
  tier: string | null;
  userEmail: string | null;
  userId: string;
}

interface AdminDomainPosture {
  domain_id: string;
  site_id: string | null;
  domain_name: string;
  status: string | null;
  created_at: string | null;
  user_id: string;
  coverage_score: number | null;
  posture_score: number | null;
  coverage_breakdown: Record<string, number> | null;
  posture_breakdown: Record<string, number> | null;
  computed_at: string | null;
}

interface IncidentSummary {
  id: string;
  created_at: string;
  updated_at: string;
  status: string;
  severity: string;
  domain_id: string | null;
  domain_name: string | null;
  signal_type: string;
  signal_summary: string | null;
  assigned_to: string | null;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_reason: string | null;
}

interface IncidentEvent {
  id: string;
  created_at: string;
  event_type: string;
  actor_type: string | null;
  actor_id: string | null;
  message: string | null;
  payload: Record<string, unknown> | null;
  details: Record<string, unknown> | null;
}

interface IncidentDetail extends IncidentSummary {
  details?: Record<string, unknown> | null;
  source?: string | null;
  protection_state?: string | null;
}

interface AdminAccount {
  userId: string;
  email: string | null;
  fullName: string | null;
  companyName: string | null;
  paymentLockBypass?: boolean | null;
  mfaMethod?: string | null;
  isTestUser?: boolean | null;
  emulatedSubscriptionStatus?: string | null;
  emulatedGraceEnd?: string | null;
  emulatedSuspendedSince?: string | null;
  accountKind?: 'standard' | 'dev';
  onboardingStatus?: string;
  serviceAgreementAcceptedAt?: string | null;
  defaultDevPriceCents?: number | null;
  defaultDevPriceCurrency?: string | null;
  defaultDevPriceId?: string | null;
  reviewedPricingMinActiveDomains?: number | null;
  reviewedPricingReassessmentNeeded?: boolean;
  reviewedPricingReassessmentFlaggedAt?: string | null;
  reviewedPricingReassessmentReason?: string | null;
  activePaidReviewedPricingDomains?: number;
  pendingInvite?: {
    id: string;
    status: string;
    expiresAt: string;
    accountKind: 'standard' | 'dev';
    devPriceCents: number | null;
  } | null;
  portfolioPricingApplication?: {
    id: string;
    signupSource: string | null;
    submissionSource: string;
    qualificationPath: string;
    businessName: string;
    companyUrl: string | null;
    roleAccountType: string;
    approximatePortfolioSize: string | null;
    currentSiteCount: number | null;
    expectedSiteCount: number;
    siteOwnershipType: string;
    useCaseSummary: string;
    primaryMarket: string | null;
    pricingNeed: string | null;
    expectedOnboardingTimeframe: string | null;
    status: 'pending_review' | 'under_review' | 'approved' | 'declined';
    reviewNotes: string | null;
    submittedAt: string;
    reviewedAt: string | null;
    reviewedByUserId: string | null;
    historyCount: number;
  } | null;
  domains: AdminDomain[];
}

interface UnlinkedManagedSite {
  id: string;
  user_id: string;
  site_url: string;
  created_at: string | null;
}

interface AdminListResponse {
  accounts: AdminAccount[];
  unlinkedSites: UnlinkedManagedSite[];
  testModeSettings?: {
    allowed: boolean;
    requiresConfirmation: boolean;
    production: boolean;
    enabled?: boolean;
  };
  adminGeoSettings?: {
    enabled: boolean;
  };
  securityControlsSettings?: {
    enabled: boolean;
    requiresConfirmation: boolean;
    production: boolean;
  };
  adminPortalFeatures?: {
    adminAiAssistantEnabled: boolean;
    adminMfaOutreachEnabled: boolean;
    requiresConfirmation: boolean;
    production: boolean;
  };
  inviteSettings?: {
    enabled: boolean;
    priceCatalog: Array<{
      priceCents: number;
      currency: string;
      stripePriceId: string;
      active: boolean;
    }>;
  };
}

interface AdminPanelProps {
  embedded?: boolean;
  statusFilterProp?: string;
  onStatusFilterChange?: (value: string) => void;
  alertDomainIds?: string[];
}

export default function AdminPanel({ embedded = false, statusFilterProp, onStatusFilterChange, alertDomainIds = [] }: AdminPanelProps) {
  const { user, loading, signOut } = useAuth();
  const { setImpersonation, clearImpersonation, isImpersonating, activeCustomerId } = useImpersonation();
  const { checking } = useRequireMfa();
  const navigate = useNavigate();
  const location = useLocation();

  const [adminTab, setAdminTab] = useState<'overview' | 'users' | 'sites' | 'incidents' | 'tools'>('overview');
  const [userSearch, setUserSearch] = useState('');
  const [siteSearch, setSiteSearch] = useState('');
  const [postureSearch, setPostureSearch] = useState('');
  const [postureFilter, setPostureFilter] = useState<'all' | 'low' | 'medium' | 'high' | 'missing'>('all');
  const [postureSort, setPostureSort] = useState<'coverage_desc' | 'coverage_asc' | 'posture_desc' | 'posture_asc' | 'updated_desc'>('coverage_desc');
  const [incidentSearch, setIncidentSearch] = useState('');
  const [incidentStatusFilter, setIncidentStatusFilter] = useState<string>('all');
  const [incidentSeverityFilter, setIncidentSeverityFilter] = useState<string>('curated');
  const [incidentAssigneeFilter, setIncidentAssigneeFilter] = useState<string>('');
  const [incidentList, setIncidentList] = useState<IncidentSummary[]>([]);
  const [incidentCounts, setIncidentCounts] = useState({ openSev1: 0, openSev2: 0, openSev3: 0, totalOpen: 0 });
  const [incidentsLoading, setIncidentsLoading] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<IncidentDetail | null>(null);
  const [incidentEvents, setIncidentEvents] = useState<IncidentEvent[]>([]);
  const [incidentDetailLoading, setIncidentDetailLoading] = useState(false);
  const [incidentNote, setIncidentNote] = useState('');
  const [incidentResolveReason, setIncidentResolveReason] = useState('');
  const [incidentResolveType, setIncidentResolveType] = useState<'RESOLVED' | 'FALSE_POSITIVE'>('RESOLVED');
  const [incidentAssignTo, setIncidentAssignTo] = useState('');
  const [incidentUpdateSeverity, setIncidentUpdateSeverity] = useState(''); 
  const [incidentUpdateSignal, setIncidentUpdateSignal] = useState('');
  const [incidentActionLoading, setIncidentActionLoading] = useState(false);
  const incidentsDebug = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('incidents_debug') === '1';
  }, [location.search]);
  const [incidentDebugInfo, setIncidentDebugInfo] = useState<{
    filters: {
      status: string[] | null;
      severity: string[] | null;
      domain: string | null;
      assignedTo: string | null;
      sort: string;
      direction: string;
    };
    resultCount: number;
    lastRefreshedAt: string;
  } | null>(null);
  const [userFilter, setUserFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>(statusFilterProp || 'all');
  const [showDeleted, setShowDeleted] = useState(false);
  const [restoringDomainId, setRestoringDomainId] = useState<string | null>(null);
  const [deletingDomainId, setDeletingDomainId] = useState<string | null>(null);
  const [confirmDomainInput, setConfirmDomainInput] = useState('');
  const [updatingBypassUserId, setUpdatingBypassUserId] = useState<string | null>(null);
  const [disablingMfaUserId, setDisablingMfaUserId] = useState<string | null>(null);
  const [updatingTestModeUserId, setUpdatingTestModeUserId] = useState<string | null>(null);
  const [pairingLinks, setPairingLinks] = useState<Record<string, { link: string; createdAt: number }>>({});
  const [pairingAltchaToken, setPairingAltchaToken] = useState<string | null>(null);
  const [wpAdminOverrides, setWpAdminOverrides] = useState<Record<string, string>>({});
  const [updatingWpAdminDomainId, setUpdatingWpAdminDomainId] = useState<string | null>(null);
  const [repairingManagedSites, setRepairingManagedSites] = useState(false);
  const [wipingDomainId, setWipingDomainId] = useState<string | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<AdminAccount | null>(null);
  const [diagnosticsDomain, setDiagnosticsDomain] = useState<{
    domain: AdminDomain;
    paymentBypass: boolean;
    managedSite?: any;
  } | null>(null);
  const [diagnosticsLogPage, setDiagnosticsLogPage] = useState(1);
  const [testModeInputs, setTestModeInputs] = useState<Record<
    string,
    { isTestUser: boolean; emulationStatus: string; graceEnd: string; suspendedSince: string; confirmPhrase: string }
  >>({});
  const [testModeSettingEnabled, setTestModeSettingEnabled] = useState(false);
  const [testModeSettingConfirm, setTestModeSettingConfirm] = useState('');
  const [savingTestModeSetting, setSavingTestModeSetting] = useState(false);
  const [adminGeoGlobalEnabled, setAdminGeoGlobalEnabled] = useState(false);
  const [savingAdminGeoGlobal, setSavingAdminGeoGlobal] = useState(false);
  const [securityControlsWriteEnabled, setSecurityControlsWriteEnabled] = useState(false);
  const [securityControlsConfirm, setSecurityControlsConfirm] = useState('');
  const [savingSecurityControlsSetting, setSavingSecurityControlsSetting] = useState(false);
  const [adminAiAssistantEnabled, setAdminAiAssistantEnabled] = useState(false);
  const [adminAiAssistantConfirm, setAdminAiAssistantConfirm] = useState('');
  const [savingAdminAiAssistantSetting, setSavingAdminAiAssistantSetting] = useState(false);
  const [adminMfaOutreachEnabled, setAdminMfaOutreachEnabled] = useState(false);
  const [adminMfaOutreachConfirm, setAdminMfaOutreachConfirm] = useState('');
  const [savingAdminMfaOutreachSetting, setSavingAdminMfaOutreachSetting] = useState(false);
  const [repairConfirm, setRepairConfirm] = useState('');
  const [policySyncTarget, setPolicySyncTarget] = useState('');
  const [policySyncLoading, setPolicySyncLoading] = useState(false);
  const [updatingHostedInstallSiteId, setUpdatingHostedInstallSiteId] = useState<string | null>(null);
  const [runningHostedAutomationDomainId, setRunningHostedAutomationDomainId] = useState<string | null>(null);
  const [updatingHostedMigrationSiteId, setUpdatingHostedMigrationSiteId] = useState<string | null>(null);
  const [policySyncResult, setPolicySyncResult] = useState<null | {
    siteId: string;
    domainId: string | null;
    domainName: string | null;
    siteUrl: string | null;
    pluginVersion: string | null;
    lastHeartbeatAt: string | null;
    policyUpdatedAt: string | null;
    policyTier: string | null;
    policyLocked: boolean | null;
    pendingCommands: Array<{ id: string; type: string; status: string; created_at: string | null }>;
    inSync: boolean;
    reasons: string[];
  }>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFullName, setInviteFullName] = useState('');
  const [inviteAccountKind, setInviteAccountKind] = useState<'standard' | 'dev'>('standard');
  const [inviteDevPriceCents, setInviteDevPriceCents] = useState<number>(500);
  const [invitingAccount, setInvitingAccount] = useState(false);
  const [resendingInviteId, setResendingInviteId] = useState<string | null>(null);
  const [reviewingPortfolioApplicationId, setReviewingPortfolioApplicationId] = useState<string | null>(null);
  const [portfolioReviewNotes, setPortfolioReviewNotes] = useState('');
  const [portfolioApprovalPriceCents, setPortfolioApprovalPriceCents] = useState<number>(500);
  const [portfolioApprovalMinimumActiveDomains, setPortfolioApprovalMinimumActiveDomains] = useState<number>(1);
  const [clearingReviewedPricingReassessmentUserId, setClearingReviewedPricingReassessmentUserId] = useState<string | null>(null);

  const handleImpersonate = (account: { userId: string; email?: string | null; fullName?: string | null }) => {
    if (isImpersonating && activeCustomerId && activeCustomerId !== account.userId) {
      clearImpersonation();
    }
    setImpersonation({
      activeCustomerId: account.userId,
      activeCustomerEmail: account.email ?? null,
      activeCustomerName: account.fullName ?? null,
    });
    navigate(`/portal/admin/impersonate/${account.userId}`);
  };

  // Check admin role
  const { data: isAdmin, isLoading: isAdminLoading } = useQuery({
    queryKey: ['isAdmin', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user!.id);
      // Check for admin or super_admin (super_admin stored but typed as admin for RLS)
      return (data || []).some((r) => r.role === 'admin');
    },
    enabled: !!user,
  });

  const { data: isSuperAdmin, isLoading: isSuperAdminLoading } = useQuery({
    queryKey: ['isSuperAdmin', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user!.id)
        .eq('role', 'super_admin')
        .maybeSingle();
      return !!data;
    },
    enabled: !!user,
  });

  // Load accounts/domains
  const { data: adminList, isLoading: accountsLoading, refetch } = useQuery<AdminListResponse>({
    queryKey: ['admin-accounts'],
    queryFn: async () => {
      try {
        const { data, error } = await invokeEdgeWithAuthRetry<any>('admin-list-accounts');
        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        // Source-of-truth fallback for rollout flags: read directly from promo_settings.
        // This prevents stale/mismatched edge payloads from rendering incorrect toggle states.
        const { data: promoSettings } = await supabase
          .from('promo_settings')
          .select('admin_ai_assistant_enabled, admin_mfa_outreach_enabled')
          .eq('id', 1)
          .maybeSingle();

        const hasPromoAiFlag = typeof promoSettings?.admin_ai_assistant_enabled === 'boolean';
        const hasPromoMfaFlag = typeof promoSettings?.admin_mfa_outreach_enabled === 'boolean';
        const adminAiAssistantEnabled = hasPromoAiFlag
          ? promoSettings!.admin_ai_assistant_enabled === true
          : data?.adminPortalFeatures?.adminAiAssistantEnabled === true;
        const adminMfaOutreachEnabled = hasPromoMfaFlag
          ? promoSettings!.admin_mfa_outreach_enabled === true
          : data?.adminPortalFeatures?.adminMfaOutreachEnabled === true;

        const adminPortalFeatures = data?.adminPortalFeatures
          ? {
              ...data.adminPortalFeatures,
              adminAiAssistantEnabled,
              adminMfaOutreachEnabled,
            }
          : hasPromoAiFlag || hasPromoMfaFlag
          ? {
              adminAiAssistantEnabled,
              adminMfaOutreachEnabled,
              requiresConfirmation: Boolean(data?.testModeSettings?.production),
              production: Boolean(data?.testModeSettings?.production),
            }
          : undefined;

        return {
          accounts: (data?.accounts || []) as AdminAccount[],
          unlinkedSites: (data?.unlinkedSites || []) as UnlinkedManagedSite[],
          testModeSettings: data?.testModeSettings || undefined,
          adminGeoSettings: data?.adminGeoSettings || undefined,
          securityControlsSettings: data?.securityControlsSettings || undefined,
          adminPortalFeatures,
          inviteSettings: data?.inviteSettings || undefined,
        };
      } catch (err: any) {
        if (isUnauthorized(err)) {
          await handleUnauthorized();
        }
        throw err;
      }
    },
    enabled: !!user && isAdmin === true,
  });
  const accounts = adminList?.accounts || [];
  const unlinkedSites = adminList?.unlinkedSites || [];
  const testModeSettings = adminList?.testModeSettings;
  const adminGeoSettings = adminList?.adminGeoSettings;
  const securityControlsSettings = adminList?.securityControlsSettings;
  const adminPortalFeatures = adminList?.adminPortalFeatures;
  const inviteSettings = adminList?.inviteSettings;

  useEffect(() => {
    if (!selectedAccount) return;
    const nextSelectedAccount = accounts.find((account) => account.userId === selectedAccount.userId) || null;
    setSelectedAccount(nextSelectedAccount);
  }, [accounts, selectedAccount?.userId]);

  const { data: adminPosture, isLoading: postureLoading } = useQuery<{ domains: AdminDomainPosture[] }>({
    queryKey: ['admin-domain-posture'],
    queryFn: async () => {
      try {
        const { data, error } = await invokeEdgeWithAuthRetry<any>('admin-domain-posture');
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        return { domains: (data?.domains || []) as AdminDomainPosture[] };
      } catch (err: any) {
        if (isUnauthorized(err)) {
          await handleUnauthorized();
        }
        throw err;
      }
    },
    enabled: !!user && isAdmin === true,
  });
  const postureDomains = adminPosture?.domains || [];

  useEffect(() => {
    if (typeof testModeSettings?.enabled === 'boolean') {
      setTestModeSettingEnabled(testModeSettings.enabled);
    }
  }, [testModeSettings?.enabled]);

  useEffect(() => {
    const activePrices = (inviteSettings?.priceCatalog || []).filter((row) => row.active);
    const defaultPrice = activePrices.find((row) => row.priceCents === 500) || activePrices[0];
    if (defaultPrice) {
      setPortfolioApprovalPriceCents(defaultPrice.priceCents);
    }
  }, [inviteSettings?.priceCatalog]);

  useEffect(() => {
    setPortfolioReviewNotes(selectedAccount?.portfolioPricingApplication?.reviewNotes || '');
  }, [selectedAccount?.portfolioPricingApplication?.id, selectedAccount?.portfolioPricingApplication?.reviewNotes]);

  useEffect(() => {
    const nextThreshold =
      typeof selectedAccount?.reviewedPricingMinActiveDomains === 'number' && selectedAccount.reviewedPricingMinActiveDomains > 0
        ? selectedAccount.reviewedPricingMinActiveDomains
        : typeof selectedAccount?.portfolioPricingApplication?.expectedSiteCount === 'number' &&
            selectedAccount.portfolioPricingApplication.expectedSiteCount > 0
          ? selectedAccount.portfolioPricingApplication.expectedSiteCount
          : 1;
    setPortfolioApprovalMinimumActiveDomains(nextThreshold);
  }, [
    selectedAccount?.userId,
    selectedAccount?.reviewedPricingMinActiveDomains,
    selectedAccount?.portfolioPricingApplication?.id,
    selectedAccount?.portfolioPricingApplication?.expectedSiteCount,
  ]);

  useEffect(() => {
    if (typeof adminGeoSettings?.enabled === 'boolean') {
      setAdminGeoGlobalEnabled(adminGeoSettings.enabled);
    }
  }, [adminGeoSettings?.enabled]);

  useEffect(() => {
    if (typeof securityControlsSettings?.enabled === 'boolean') {
      setSecurityControlsWriteEnabled(securityControlsSettings.enabled);
    }
  }, [securityControlsSettings?.enabled]);

  useEffect(() => {
    if (typeof adminPortalFeatures?.adminAiAssistantEnabled === 'boolean') {
      setAdminAiAssistantEnabled(adminPortalFeatures.adminAiAssistantEnabled);
    }
    if (typeof adminPortalFeatures?.adminMfaOutreachEnabled === 'boolean') {
      setAdminMfaOutreachEnabled(adminPortalFeatures.adminMfaOutreachEnabled);
    }
  }, [adminPortalFeatures?.adminAiAssistantEnabled, adminPortalFeatures?.adminMfaOutreachEnabled]);

  useEffect(() => {
    const activePrices = (inviteSettings?.priceCatalog || []).filter((row) => row.active);
    if (activePrices.length === 0) return;
    const defaultPrice = activePrices.find((row) => row.priceCents === 500) || activePrices[0];
    setInviteDevPriceCents(defaultPrice.priceCents);
  }, [inviteSettings?.priceCatalog]);

  // Load deleted domains
  const { data: deletedDomains, isLoading: deletedLoading, refetch: refetchDeleted } = useQuery({
    queryKey: ['admin-deleted-domains'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase.functions.invoke('admin-deleted-domains', {
          body: { action: 'list' },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        return (data?.domains || []) as DeletedDomain[];
      } catch (err: any) {
        if (isUnauthorized(err)) {
          await handleUnauthorized();
        }
        throw err;
      }
    },
    enabled: !!user && isAdmin === true && showDeleted,
  });

  useEffect(() => {
    if (!checking && !loading && isAdmin === false) {
      navigate('/portal');
    }
  }, [checking, loading, isAdmin, navigate]);

  useEffect(() => {
    if (statusFilterProp && statusFilterProp !== statusFilter) {
      setStatusFilter(statusFilterProp);
    }
  }, [statusFilterProp, statusFilter]);

  useEffect(() => {
    if (adminTab !== 'incidents') return;
    fetchIncidents();
  }, [adminTab, incidentSearch, incidentStatusFilter, incidentSeverityFilter, incidentAssigneeFilter]);


  const alertDomainSet = useMemo(() => new Set(alertDomainIds || []), [alertDomainIds]);
  const isPaymentNeeded = (status?: string | null) =>
    status === 'payment_needed' || status === 'payment_incomplete' || status === 'pending_payment';

  const filteredAccounts = useMemo(() => {
    if (!accounts) return [];
    const q = userSearch.toLowerCase();
    const matchesUserFilter = (account: AdminAccount) => {
      if (userFilter === 'all') return true;
      const hasPaymentBypass = Boolean(account.paymentLockBypass);
      const isTestUser = Boolean(account.isTestUser);
      const hasPastDue = account.domains.some((d) => isPaymentNeeded(d.status));
      const hasSuspended = account.domains.some((d) => d.status === 'cancelled');
      const hasDeleted = account.domains.some((d) => d.status === 'deleted');
      if (userFilter === 'past_due') return hasPastDue;
      if (userFilter === 'suspended') return hasSuspended;
      if (userFilter === 'payment_bypass') return hasPaymentBypass;
      if (userFilter === 'test_users') return isTestUser;
      if (userFilter === 'deleted') return hasDeleted;
      return true;
    };

    return accounts
      .filter((account) => {
        if (!matchesUserFilter(account)) return false;
        if (!q) return true;
        const emailValue = account.email?.toLowerCase() || '';
        const nameValue = account.fullName?.toLowerCase() || '';
        const companyValue = account.companyName?.toLowerCase() || '';
        const matchesAccount =
          emailValue.includes(q) ||
          nameValue.includes(q) ||
          companyValue.includes(q);
        if (matchesAccount) return true;
        return account.domains.some((domain) => domain.domainName.toLowerCase().includes(q));
      });
  }, [accounts, userSearch, userFilter]);

  const allDomainIds = useMemo(
    () => accounts?.flatMap((account) => account.domains.map((domain) => domain.id)) ?? [],
    [accounts],
  );

  const { data: managedSites } = useQuery({
    queryKey: ['admin-managed-sites', allDomainIds.join(',')],
    queryFn: async () => {
      if (allDomainIds.length === 0) return [];
      const { data, error } = await (supabase
        .from('managed_sites' as any)
        .select('id, domain_id, site_url, wp_admin_url, wp_admin_url_locked, last_heartbeat_at, plugin_version, can_install_plugins, plugin_install_source, plugin_install_status, plugin_install_attempted_at, plugin_install_verified_at, plugin_install_error, hosted_provider, hosted_whmcs_client_id, hosted_whmcs_service_id, hosted_whmcs_order_id, hosted_automation_status, hosted_automation_last_run_at, hosted_automation_error, hosted_is_migration, hosted_migration_confirmed_at, hosted_migration_confirmed_by') as any)
        .in('domain_id', allDomainIds);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user && isAdmin === true && allDomainIds.length > 0,
  });

  const managedSiteByDomainId = useMemo(() => {
    const map = new Map<string, any>();
    const siteUrlMap = new Map<string, any>();
    (managedSites || []).forEach((site: any) => {
      if (site.domain_id) map.set(site.domain_id, site);
      const normalizedSite = normalizeDomainValue(site.site_url);
      if (normalizedSite) siteUrlMap.set(normalizedSite, site);
    });
    return { byDomainId: map, bySiteUrl: siteUrlMap };
  }, [managedSites]);

  const flattenedDomains = useMemo(() => {
    return (accounts || []).flatMap((account) =>
      account.domains.map((domain) => ({
        account,
        domain,
        managedSite:
          managedSiteByDomainId.byDomainId.get(domain.id) ||
          managedSiteByDomainId.bySiteUrl.get(normalizeDomainValue(domain.domainName) || ''),
      })),
    );
  }, [accounts, managedSiteByDomainId]);

  const filteredSites = useMemo(() => {
    const query = siteSearch.toLowerCase();
    const matchesStatus = (domain: AdminDomain) => {
      if (statusFilter === 'alerts') return alertDomainSet.has(domain.id);
      if (statusFilter === 'all') return true;
      if (statusFilter === 'disconnected') return true;
      return domain.status === statusFilter;
    };
    return flattenedDomains.filter((entry) => {
      if (!showDeleted && entry.domain.status === 'deleted') return false;
      if (!matchesStatus(entry.domain)) return false;
      if (statusFilter === 'disconnected') {
        return !isHeartbeatFresh(entry.managedSite?.last_heartbeat_at);
      }
      if (!query) return true;
      const emailValue = entry.account.email?.toLowerCase() || '';
      const siteUrlValue = entry.managedSite?.site_url?.toLowerCase() || '';
      return entry.domain.domainName.toLowerCase().includes(query) ||
        emailValue.includes(query) ||
        siteUrlValue.includes(query);
    });
  }, [flattenedDomains, statusFilter, siteSearch, showDeleted, alertDomainSet]);

  const incidentTarget = useMemo(() => {
    const query = incidentSearch.trim();
    if (!query) return null;
    const normalizedQuery = normalizeDomainValue(query) || query.toLowerCase();
    return (
      flattenedDomains.find((entry) => {
        const domainMatch = entry.domain.domainName.toLowerCase().includes(normalizedQuery);
        const siteUrlValue = normalizeDomainValue(entry.managedSite?.site_url) || '';
        const siteMatch = siteUrlValue !== '' && siteUrlValue.includes(normalizedQuery);
        return domainMatch || siteMatch;
      }) || null
    );
  }, [incidentSearch, flattenedDomains]);
  const incidentHeartbeatStale = incidentTarget ? !isHeartbeatFresh(incidentTarget.managedSite?.last_heartbeat_at) : false;

  const filteredPostureDomains = useMemo(() => {
    const query = postureSearch.trim().toLowerCase();
    const scoreBucket = (score?: number | null) => {
      if (typeof score !== 'number') return 'missing';
      if (score >= 80) return 'high';
      if (score >= 50) return 'medium';
      return 'low';
    };

    const filtered = postureDomains.filter((domain) => {
      if (query && !domain.domain_name.toLowerCase().includes(query)) return false;
      const coverageBucket = scoreBucket(domain.coverage_score);
      const postureBucket = scoreBucket(domain.posture_score);
      if (postureFilter === 'missing') {
        return coverageBucket === 'missing' || postureBucket === 'missing';
      }
      if (postureFilter === 'low') {
        return coverageBucket === 'low' || postureBucket === 'low';
      }
      if (postureFilter === 'medium') {
        return coverageBucket === 'medium' || postureBucket === 'medium';
      }
      if (postureFilter === 'high') {
        return coverageBucket === 'high' && postureBucket === 'high';
      }
      return true;
    });

    const sorted = [...filtered];
    sorted.sort((a, b) => {
      switch (postureSort) {
        case 'coverage_asc':
          return (a.coverage_score ?? -1) - (b.coverage_score ?? -1);
        case 'coverage_desc':
          return (b.coverage_score ?? -1) - (a.coverage_score ?? -1);
        case 'posture_asc':
          return (a.posture_score ?? -1) - (b.posture_score ?? -1);
        case 'posture_desc':
          return (b.posture_score ?? -1) - (a.posture_score ?? -1);
        case 'updated_desc':
          return new Date(b.computed_at || 0).getTime() - new Date(a.computed_at || 0).getTime();
        default:
          return 0;
      }
    });
    return sorted;
  }, [postureDomains, postureSearch, postureFilter, postureSort]);

  const needsAttention = useMemo(() => {
    const items = flattenedDomains
      .map((entry) => {
        const { domain, managedSite, account } = entry;
        if (domain.status === 'deleted') {
          return null;
        }
        const lastCheckIn = managedSite?.last_heartbeat_at
          ? new Date(managedSite.last_heartbeat_at).toLocaleString()
          : 'Never';
        if (isPaymentNeeded(domain.status)) {
          return {
            id: domain.id,
            domain,
            account,
            lastCheckIn,
            issue: 'Billing past due',
            action: 'View user',
            actionType: 'user',
          };
        }
        if (!isHeartbeatFresh(managedSite?.last_heartbeat_at)) {
          return {
            id: domain.id,
            domain,
            account,
            lastCheckIn,
            issue: domain.status === 'pending' ? 'Awaiting connection' : 'Missing heartbeat',
            action: 'View site',
            actionType: 'site',
          };
        }
        if (alertDomainSet.has(domain.id)) {
          return {
            id: domain.id,
            domain,
            account,
            lastCheckIn,
            issue: 'Issue requiring action',
            action: 'View site',
            actionType: 'site',
          };
        }
        return null;
      })
      .filter(Boolean) as Array<{
        id: string;
        domain: AdminDomain;
        account: AdminAccount;
        lastCheckIn: string;
        issue: string;
        action: string;
        actionType: 'user' | 'site';
      }>;
    return items.slice(0, 10);
  }, [flattenedDomains, alertDomainSet]);

  const connectedCount = useMemo(() => {
    return flattenedDomains.filter((entry) => isHeartbeatFresh(entry.managedSite?.last_heartbeat_at)).length;
  }, [flattenedDomains]);

  const issuesCount = useMemo(() => alertDomainSet.size, [alertDomainSet]);

  const subscriptionIssueCount = useMemo(() => {
    return flattenedDomains.filter((entry) => isPaymentNeeded(entry.domain.status)).length;
  }, [flattenedDomains]);

  const errorCount = useMemo(() => {
    return flattenedDomains.filter((entry) => {
      const managedSite = entry.managedSite;
      return entry.domain.status === 'active' && !isHeartbeatFresh(managedSite?.last_heartbeat_at);
    }).length;
  }, [flattenedDomains]);

  const provisioningCount = useMemo(() => {
    return flattenedDomains.filter((entry) => {
      if (entry.domain.status !== 'pending') return false;
      return !isHeartbeatFresh(entry.managedSite?.last_heartbeat_at);
    }).length;
  }, [flattenedDomains]);


  const diagnosticsQuery = useQuery({
    queryKey: ['admin-diagnostics', diagnosticsDomain?.domain.id],
    queryFn: async () => {
      const session = (await supabase.auth.getSession()).data.session;
      const { data, error } = await supabase.functions.invoke('admin-diagnostics', {
        headers: { Authorization: `Bearer ${session?.access_token || ''}` },
        body: { domainId: diagnosticsDomain?.domain.id },
      });
      if (error || data?.error) throw new Error(error?.message || data?.error || 'Unable to load diagnostics');
      return data as {
        sites: any[];
        heartbeatLogs: any[];
        commandResults?: any[];
      };
    },
    enabled: !!user && isAdmin === true && !!diagnosticsDomain,
  });

  const diagnosticsSite = diagnosticsQuery.data?.sites?.[0];
  const diagnosticsLogs = diagnosticsQuery.data?.heartbeatLogs || [];
  const diagnosticsCommands = diagnosticsQuery.data?.commandResults || [];
  const diagnosticsReport = useMemo(
    () => parseConnectorReport(diagnosticsSite?.connector_report),
    [diagnosticsSite?.connector_report],
  );
  const diagnosticsLogsPerPage = 10;
  const diagnosticsLogPages = Math.max(1, Math.ceil(diagnosticsLogs.length / diagnosticsLogsPerPage));
  const diagnosticsLogPageSafe = Math.min(diagnosticsLogPage, diagnosticsLogPages);
  const diagnosticsLogsSliceStart = (diagnosticsLogPageSafe - 1) * diagnosticsLogsPerPage;
  const diagnosticsLogsSlice = diagnosticsLogs.slice(
    diagnosticsLogsSliceStart,
    diagnosticsLogsSliceStart + diagnosticsLogsPerPage,
  );

  const diagnosticsTargetSite = diagnosticsDomain?.managedSite || diagnosticsSite;

  useEffect(() => {
    if (diagnosticsDomain) {
      setDiagnosticsLogPage(1);
    }
  }, [diagnosticsDomain]);

  const saveTextFile = (filename: string, contents: string) => {
    const blob = new Blob([contents], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const enqueueSiteCommand = async (siteId: string, type: string, payload: Record<string, unknown> = {}) => {
    const { error } = await (supabase
      .from('site_commands' as any) as any)
      .insert({ site_id: siteId, type, payload });
    if (error) throw error;
  };

  const handleSelfCheck = async (managedSite?: any) => {
    if (!managedSite?.id) {
      toast.error('Managed site not found.');
      return;
    }
    try {
      await enqueueSiteCommand(managedSite.id, 'run_inventory');
      toast.success('Status refresh queued. Updates will appear shortly.');
    } catch (err: any) {
      console.error('status refresh failed:', err);
      toast.error(err?.message || 'Unable to queue status refresh.');
    }
  };

  const handleDebugFetchLogs = async (managedSite?: any) => {
    if (!managedSite?.id) {
      toast.error('Managed site not found.');
      return;
    }
    try {
      await enqueueSiteCommand(managedSite.id, 'debug_fetch_logs');
      toast.success('Log collection queued.');
    } catch (err: any) {
      console.error('debug_fetch_logs failed:', err);
      toast.error(err?.message || 'Unable to queue log collection.');
    }
  };

  const handleDebugInstallMu = async (managedSite?: any) => {
    if (!managedSite?.id) {
      toast.error('Managed site not found.');
      return;
    }
    if (!confirm('Install MU Sentry helper on this site?')) {
      return;
    }
    try {
      await enqueueSiteCommand(managedSite.id, 'debug_install_mu_helper');
      toast.success('MU helper install queued.');
    } catch (err: any) {
      console.error('debug_install_mu_helper failed:', err);
      toast.error(err?.message || 'Unable to queue MU helper install.');
    }
  };

  const handleDebugRemoveMu = async (managedSite?: any) => {
    if (!managedSite?.id) {
      toast.error('Managed site not found.');
      return;
    }
    if (!confirm('Remove MU Sentry helper from this site?')) {
      return;
    }
    try {
      await enqueueSiteCommand(managedSite.id, 'debug_remove_mu_helper');
      toast.success('MU helper removal queued.');
    } catch (err: any) {
      console.error('debug_remove_mu_helper failed:', err);
      toast.error(err?.message || 'Unable to queue MU helper removal.');
    }
  };

  const handleUpdateWpAdminUrl = async (domain: AdminDomain) => {
    const rawValue = wpAdminOverrides[domain.id]?.trim();
    if (!rawValue) {
      toast.error('Enter a full admin URL to continue.');
      return;
    }

    setUpdatingWpAdminDomainId(domain.id);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('admin-set-wp-admin-url', {
        headers: { Authorization: `Bearer ${token}` },
        body: {
          domainId: domain.id,
          wpAdminUrl: rawValue,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success('WP Admin URL updated.');
      setWpAdminOverrides((prev) => ({ ...prev, [domain.id]: data.wp_admin_url || rawValue }));
      refetch();
    } catch (err: unknown) {
      console.error('admin-set-wp-admin-url failed:', err);
      toast.error(await formatEdgeFunctionError(err, 'Unable to update admin URL.'));
    } finally {
      setUpdatingWpAdminDomainId(null);
    }
  };

  const handleWipeConnections = async (domain: AdminDomain) => {
    if (!confirm(`Wipe all connections for ${domain.domainName}? This forces a full re-onboarding.`)) {
      return;
    }
    setWipingDomainId(domain.id);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const { data, error } = await supabase.functions.invoke('admin-wipe-connections', {
        headers: { Authorization: `Bearer ${session?.access_token || ''}` },
        body: { domainId: domain.id },
      });
      if (error || data?.error) throw new Error(error?.message || data?.error || 'Unable to wipe connections');
      toast.success('Connections wiped. Generate a new pairing key to restart onboarding.');
      refetch();
    } catch (err: any) {
      console.error('admin-wipe-connections failed:', err);
      toast.error(err?.message || 'Unable to wipe connections.');
    } finally {
      setWipingDomainId(null);
    }
  };

  const handleHostedInstallStatusUpdate = async (
    managedSite: any,
    pluginInstallSource: 'manual' | 'host_assisted',
    pluginInstallStatus: 'manual' | 'host_assisted_pending' | 'host_assisted_verified' | 'install_failed',
    pluginInstallError?: string,
  ) => {
    if (!managedSite?.id) {
      toast.error('Managed site not found.');
      return;
    }

    setUpdatingHostedInstallSiteId(managedSite.id);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const { data, error } = await supabase.functions.invoke('admin-set-hosted-plugin-install-status', {
        headers: { Authorization: `Bearer ${session?.access_token || ''}` },
        body: {
          siteId: managedSite.id,
          pluginInstallSource,
          pluginInstallStatus,
          pluginInstallError,
        },
      });

      if (error || data?.error) {
        throw new Error(error?.message || data?.error || 'Unable to update hosted install status');
      }

      toast.success('Hosted install status updated.');
      refetch();
    } catch (err: any) {
      console.error('admin-set-hosted-plugin-install-status failed:', err);
      toast.error(err?.message || 'Unable to update hosted install status.');
    } finally {
      setUpdatingHostedInstallSiteId(null);
    }
  };

  const handleRunHostedAutomation = async (domain: AdminDomain, managedSite?: any) => {
    setRunningHostedAutomationDomainId(domain.id);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const { data, error } = await supabase.functions.invoke('hosted-wordpress-automation', {
        headers: { Authorization: `Bearer ${session?.access_token || ''}` },
        body: managedSite?.id ? { siteId: managedSite.id } : { domainId: domain.id },
      });

      const result = data?.result;
      if (error || data?.error || result?.success === false) {
        throw new Error(
          error?.message ||
            data?.error ||
            result?.message ||
            'Unable to run hosted WordPress automation',
        );
      }

      toast.success(result?.message || 'Hosted WordPress automation dispatched.');
      refetch();
    } catch (err: any) {
      console.error('hosted-wordpress-automation failed:', err);
      toast.error(err?.message || 'Unable to run hosted WordPress automation.');
    } finally {
      setRunningHostedAutomationDomainId(null);
    }
  };

  const handleHostedMigrationAction = async (managedSite: any, action: 'confirm' | 'reset') => {
    if (!managedSite?.id) {
      toast.error('Managed site not found.');
      return;
    }

    setUpdatingHostedMigrationSiteId(managedSite.id);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const { data, error } = await supabase.functions.invoke('confirm-hosted-migration', {
        headers: { Authorization: `Bearer ${session?.access_token || ''}` },
        body: action === 'reset' ? { siteId: managedSite.id, action: 'reset' } : { siteId: managedSite.id },
      });

      if (error || data?.error) {
        throw new Error(error?.message || data?.error || 'Unable to update hosted migration state.');
      }

      toast.success(
        data?.message ||
          (action === 'reset'
            ? 'Hosted migration confirmation reset.'
            : 'Hosted migration confirmed and automation resumed.'),
      );
      refetch();
    } catch (err: any) {
      console.error('confirm-hosted-migration failed:', err);
      toast.error(err?.message || 'Unable to update hosted migration state.');
    } finally {
      setUpdatingHostedMigrationSiteId(null);
    }
  };

  const handleRepairManagedSites = async () => {
    setRepairingManagedSites(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-repair-managed-sites', {
        body: {},
      });
      if (error || data?.error) {
        throw new Error(error?.message || data?.error || 'Repair failed');
      }
      toast.success(
        `Repaired ${data.repaired || 0} managed site(s). Skipped ${data.skipped || 0}.`
      );
      setRepairConfirm('');
      await refetch();
    } catch (err: unknown) {
      console.error('admin-repair-managed-sites failed:', err);
      const message = err instanceof Error ? err.message : 'Unable to repair managed sites.';
      toast.error(message);
    } finally {
      setRepairingManagedSites(false);
    }
  };

  const handlePolicySyncCheck = async () => {
    const target = policySyncTarget.trim();
    if (!target) {
      toast.error('Enter a site ID or domain ID.');
      return;
    }
    setPolicySyncLoading(true);
    setPolicySyncResult(null);
    try {
      let resolvedSite: {
        id: string;
        domain_id: string | null;
        site_url: string | null;
        plugin_version: string | null;
        last_heartbeat_at: string | null;
      } | null = null;

      const { data: byId, error: byIdError } = await supabase
        .from('managed_sites')
        .select('id, domain_id, site_url, plugin_version, last_heartbeat_at')
        .eq('id', target)
        .maybeSingle();
      if (byIdError) throw byIdError;
      if (byId) {
        resolvedSite = byId;
      }

      if (!resolvedSite) {
        const { data: byDomain, error: byDomainError } = await supabase
          .from('managed_sites')
          .select('id, domain_id, site_url, plugin_version, last_heartbeat_at')
          .eq('domain_id', target)
          .order('last_heartbeat_at', { ascending: false })
          .limit(5);
        if (byDomainError) throw byDomainError;
        if (byDomain && byDomain.length > 0) {
          resolvedSite = byDomain[0];
        }
      }

      if (!resolvedSite) {
        toast.error('No managed site found for that ID.');
        return;
      }

      const { data: domainRow } = await supabase
        .from('domains')
        .select('domain_name')
        .eq('id', resolvedSite.domain_id ?? '')
        .maybeSingle();

      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      const token = sessionData.session?.access_token;
      if (!token) {
        throw new Error('Not authenticated');
      }
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) throw new Error('Supabase URL not configured');
      const policyResponse = await fetch(
        `${supabaseUrl}/functions/v1/site-policy?site_id=${resolvedSite.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-SS-Auth': 'user',
          },
        }
      );
      const policyData = await policyResponse.json().catch(() => ({}));
      if (policyResponse.status === 403) {
        console.warn('[site-policy] forbidden response', policyData);
      }
      if (!policyResponse.ok) {
        throw new Error(policyData?.error || 'Unable to load policy');
      }

      const policyUpdatedAt = policyData?.updated_at ?? null;
      const policyTier = typeof policyData?.policy?.tier === 'string' ? policyData.policy.tier : null;
      const policyLocked = typeof policyData?.policy?.locked === 'boolean' ? policyData.policy.locked : null;

      const { data: pendingCommands, error: pendingError } = await supabase
        .from('site_commands')
        .select('id, type, status, created_at')
        .eq('site_id', resolvedSite.id)
        .in('status', ['queued', 'processing'])
        .order('created_at', { ascending: false })
        .limit(20);
      if (pendingError) throw pendingError;

      const pending = pendingCommands || [];
      const lastHeartbeatAt = resolvedSite.last_heartbeat_at ?? null;

      const reasons: string[] = [];
      if (!policyUpdatedAt) reasons.push('Policy has not been saved yet.');
      if (!lastHeartbeatAt) reasons.push('No heartbeat received yet.');
      if (policyUpdatedAt && lastHeartbeatAt && Date.parse(lastHeartbeatAt) < Date.parse(policyUpdatedAt)) {
        reasons.push('Latest heartbeat predates policy update.');
      }
      if (pending.length > 0) reasons.push(`${pending.length} pending site command(s) queued.`);

      const inSync = reasons.length === 0;

      setPolicySyncResult({
        siteId: resolvedSite.id,
        domainId: resolvedSite.domain_id ?? null,
        domainName: domainRow?.domain_name ?? null,
        siteUrl: resolvedSite.site_url ?? null,
        pluginVersion: resolvedSite.plugin_version ?? null,
        lastHeartbeatAt,
        policyUpdatedAt,
        policyTier,
        policyLocked,
        pendingCommands: pending,
        inSync,
        reasons,
      });
    } catch (error: any) {
      toast.error(error?.message ?? 'Policy sync check failed');
    } finally {
      setPolicySyncLoading(false);
    }
  };

  const handleBulkRefresh = async () => {
    const targets = filteredSites
      .map((entry) => entry.managedSite?.id)
      .filter(Boolean) as string[];
    if (targets.length === 0) {
      toast.error('No connected sites available for refresh.');
      return;
    }
    try {
      await Promise.allSettled(
        targets.map((siteId) => enqueueSiteCommand(siteId, 'run_inventory')),
      );
      toast.success(`Queued status refresh for ${targets.length} site(s).`);
    } catch (err: any) {
      console.error('Bulk refresh failed:', err);
      toast.error(err?.message || 'Unable to queue refresh.');
    }
  };

  const exportSitesList = () => {
    if (filteredSites.length === 0) {
      toast.error('No sites to export.');
      return;
    }
    const rows = [
      ['Domain', 'User Email', 'Status', 'Tier', 'Last Heartbeat', 'Site URL'],
      ...filteredSites.map(({ domain, account, managedSite }) => ([
        domain.domainName,
        account.email || '',
        domain.status || '',
        domain.tier || '',
        managedSite?.last_heartbeat_at || '',
        managedSite?.site_url || '',
      ])),
    ];
    const csv = rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    saveTextFile('shadowscan-sites.csv', csv);
  };

  const renderReportValue = (value?: string) => {
    if (!value) {
      return <span className="text-xs text-muted-foreground">Unknown</span>;
    }
    const normalized = value.toLowerCase();
    if (normalized === 'enabled') {
      return <Badge variant="success">Enabled</Badge>;
    }
    if (normalized === 'disabled') {
      return <Badge variant="neutral">Disabled</Badge>;
    }
    return <span className="text-xs text-muted-foreground">{value}</span>;
  };

  const renderAdminControls = (domain: AdminDomain, managedSite: any, paymentBypass: boolean) => {
    const heartbeatConnected = isHeartbeatFresh(managedSite?.last_heartbeat_at);
    const currentWpAdminUrl = managedSite?.wp_admin_url || domain.wpAdminUrl || '';
    const wpAdminLocked = Boolean(managedSite?.wp_admin_url_locked);
    const normalizedDomain = normalizeDomainValue(domain.domainName);
    const normalizedSiteUrl = normalizeDomainValue(managedSite?.site_url);
    const hasMismatch =
      Boolean(normalizedDomain && normalizedSiteUrl) &&
      normalizedDomain.includes('.') &&
      normalizedSiteUrl.includes('.') &&
      normalizedDomain !== normalizedSiteUrl;
    const linkedByUrl =
      !managedSiteByDomainId.byDomainId.get(domain.id) &&
      Boolean(managedSiteByDomainId.bySiteUrl.get(normalizeDomainValue(domain.domainName) || ''));
    const installTracking = resolveHostedPluginInstall(managedSite);
    const automationTracking = resolveHostedAutomation(managedSite);
    const installTrackingBusy = updatingHostedInstallSiteId === managedSite?.id;
    const automationBusy = runningHostedAutomationDomainId === domain.id;
    const migrationBusy = updatingHostedMigrationSiteId === managedSite?.id;
    const migrationPending = Boolean(managedSite?.hosted_is_migration && !managedSite?.hosted_migration_confirmed_at);

    return (
      <div className="mt-4 border-t border-border/50 pt-4 space-y-4">
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-lg border border-border/50 bg-card/40 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Download className={`h-4 w-4 ${heartbeatConnected ? 'text-[hsl(var(--ss-success))]' : 'text-muted-foreground'}`} />
              <p className="text-sm font-semibold">Plugin & pairing</p>
            </div>
            <p className="text-xs text-muted-foreground">
              Status: {heartbeatConnected ? 'Connected' : 'Not connected'} · Last check-in{' '}
              {managedSite?.last_heartbeat_at
                ? new Date(managedSite.last_heartbeat_at).toLocaleString()
                : '-'}
            </p>
            <div className="text-xs text-muted-foreground space-y-1">
              <div>Step 1: Install plugin (from WordPress.org)</div>
              <div>Step 2: Generate pairing key</div>
              <div>Step 3: Complete setup</div>
              <div>Step 4: All done - monitoring activates after billing if needed</div>
            </div>
            {!installTracking.visibleInPortal ? (
              <p className="text-xs text-muted-foreground">
                Hosted install tracking has not been set for this site yet.
              </p>
            ) : (
              <div className="text-xs text-muted-foreground space-y-1">
                <div>Hosted install: {installTracking.label}</div>
                <div>{installTracking.description}</div>
                {installTracking.error && (
                  <div className="text-amber-400">{installTracking.error}</div>
                )}
              </div>
            )}
            {automationTracking.visibleInPortal && (
              <div className="rounded-md border border-border/50 bg-muted/20 p-2 text-xs text-muted-foreground space-y-1">
                <div>Hosted automation: {automationTracking.label}</div>
                <div>{automationTracking.description}</div>
                {automationTracking.lastRunAt && (
                  <div>Last run: {new Date(automationTracking.lastRunAt).toLocaleString()}</div>
                )}
                {managedSite?.hosted_provider && <div>Provider: {managedSite.hosted_provider}</div>}
                {managedSite?.hosted_whmcs_service_id && (
                  <div>
                    WHMCS service #{managedSite.hosted_whmcs_service_id}
                    {managedSite?.hosted_whmcs_order_id ? ` · order #${managedSite.hosted_whmcs_order_id}` : ''}
                  </div>
                )}
                {automationTracking.error && (
                  <div className="text-amber-400">{automationTracking.error}</div>
                )}
              </div>
            )}
            {managedSite?.hosted_is_migration && (
              <div className="rounded-md border border-border/50 bg-muted/20 p-2 text-xs text-muted-foreground space-y-1">
                <div>Hosted migration: {migrationPending ? 'Awaiting confirmation' : 'Confirmed'}</div>
                {managedSite?.hosted_migration_confirmed_at ? (
                  <div>Confirmed at: {new Date(managedSite.hosted_migration_confirmed_at).toLocaleString()}</div>
                ) : (
                  <div>Protection stays paused until the migrated WordPress site is live and migration is confirmed.</div>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={automationBusy}
                onClick={() => handleRunHostedAutomation(domain, managedSite)}
              >
                {automationBusy
                  ? 'Running…'
                  : automationTracking.status === 'failed'
                    ? 'Retry hosted automation'
                    : 'Run hosted automation'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!managedSite?.id || installTrackingBusy}
                onClick={() => handleHostedInstallStatusUpdate(managedSite, 'manual', 'manual')}
              >
                {installTrackingBusy ? 'Saving…' : 'Set manual path'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!managedSite?.id || installTrackingBusy}
                onClick={() => handleHostedInstallStatusUpdate(managedSite, 'host_assisted', 'host_assisted_pending')}
              >
                {installTrackingBusy ? 'Saving…' : 'Mark host-assisted pending'}
              </Button>
              {managedSite?.hosted_is_migration && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!managedSite?.id || migrationBusy || !migrationPending}
                  onClick={() => handleHostedMigrationAction(managedSite, 'confirm')}
                >
                  {migrationBusy ? 'Saving…' : 'Confirm migration'}
                </Button>
              )}
              {managedSite?.hosted_is_migration && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!managedSite?.id || migrationBusy}
                  onClick={() => handleHostedMigrationAction(managedSite, 'reset')}
                >
                  {migrationBusy ? 'Saving…' : 'Reset migration confirmation'}
                </Button>
              )}
            </div>
            {managedSite?.plugin_version && (
              <p className="text-xs text-muted-foreground">Plugin version {managedSite.plugin_version}</p>
            )}
            {(() => {
              const pairingEntry = pairingLinks[domain.id];
              if (!pairingEntry) {
                return (
                  <p className="text-xs text-muted-foreground">
                    Generate a pairing key for the customer to complete setup.
                  </p>
                );
              }
              const isExpired = Date.now() - pairingEntry.createdAt > 10 * 60 * 1000;
              if (isExpired) {
                return (
                  <p className="text-xs text-muted-foreground">
                    Pairing key expired. Generate a new one to continue.
                  </p>
                );
              }
              const pairingCode = getPairingCode(pairingEntry.link) || pairingEntry.link;
              return (
                <div className="flex flex-wrap items-center gap-2 text-xs text-[hsl(var(--ss-success))]">
                  <span>Pairing key: {pairingCode}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopyPairingKey(pairingCode)}
                  >
                    Copy key
                  </Button>
                </div>
              );
            })()}
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-full rounded-md border border-border/50 bg-muted/20 p-2 space-y-1">
                <p className="text-xs text-muted-foreground">Complete verification before generating pairing keys.</p>
                <AltchaWidget onToken={setPairingAltchaToken} />
                <p className="text-xs text-muted-foreground">
                  Status: {pairingAltchaToken ? 'Verified' : 'Pending verification'}
                </p>
              </div>
              <Button asChild variant="outline" size="sm">
                <a href={SHADOWSCAN_WORDPRESS_PLUGIN.wordpressOrgUrl} target="_blank" rel="noreferrer">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  WordPress.org listing
                </a>
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => handleGeneratePairingLink(domain)}
              >
                <Link2 className="h-4 w-4 mr-2" />
                Generate pairing key
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleWipeConnections(domain)}
                disabled={wipingDomainId === domain.id}
              >
                {wipingDomainId === domain.id ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RotateCcw className="h-4 w-4 mr-2" />
                )}
                Wipe connections
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-border/50 bg-card/40 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm font-semibold">WP Admin URL</p>
            </div>
            <p className="text-xs text-muted-foreground">
              Current: {currentWpAdminUrl || 'Not set'} · Auto-updates: {wpAdminLocked ? 'Locked' : 'Unlocked'}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={wpAdminOverrides[domain.id] ?? currentWpAdminUrl}
                onChange={(e) =>
                  setWpAdminOverrides((prev) => ({ ...prev, [domain.id]: e.target.value }))
                }
                placeholder="https://example.com/wp-admin/"
                className="w-full sm:w-64"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleUpdateWpAdminUrl(domain)}
                disabled={updatingWpAdminDomainId === domain.id}
              >
                {updatingWpAdminDomainId === domain.id ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <ExternalLink className="h-4 w-4 mr-2" />
                )}
                Force admin URL
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setUpdatingWpAdminDomainId(domain.id);
                  supabase.functions.invoke('admin-set-wp-admin-url', {
                    body: { domainId: domain.id, lock: false },
                  }).then(({ error }) => {
                    if (error) throw error;
                    toast.success('WP Admin auto-updates unlocked.');
                    refetch();
                  }).catch((err: any) => {
                    console.error('unlock wp admin failed:', err);
                    toast.error(err?.message || 'Unable to unlock admin URL updates.');
                  }).finally(() => {
                    setUpdatingWpAdminDomainId(null);
                  });
                }}
                disabled={!wpAdminLocked || updatingWpAdminDomainId === domain.id}
              >
                Unlock auto-updates
              </Button>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border/50 bg-card/40 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className={`h-4 w-4 ${heartbeatConnected ? 'text-[hsl(var(--ss-success))]' : 'text-muted-foreground'}`} />
            <p className="text-sm font-semibold">Connector health</p>
          </div>
          <p className="text-xs text-muted-foreground">
            Status: {heartbeatConnected ? 'Reporting' : 'Awaiting heartbeat'}
            {managedSite?.last_heartbeat_at
              ? ` · Last check-in ${new Date(managedSite.last_heartbeat_at).toLocaleString()}`
              : ''}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleSelfCheck(managedSite)}
              disabled={!managedSite?.id}
            >
              Refresh status
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDiagnosticsDomain({ domain, paymentBypass, managedSite })}
            >
              <Activity className="h-4 w-4 mr-2" />
              Diagnostics
            </Button>
          </div>
        </div>

        {hasMismatch && (
          <Badge
            variant="danger"
            className="w-fit"
            title={`Reported: ${managedSite?.site_url || 'unknown'} · Expected: ${domain.domainName}`}
          >
            Mismatch detected
          </Badge>
        )}
        {linkedByUrl && (
          <Badge
            variant="info"
            className="w-fit"
            title="Connected via site_url because domain_id is missing."
          >
            Linked by URL
          </Badge>
        )}
      </div>
    );
  };

  const handleGeneratePairingLink = async (domain: AdminDomain) => {
    if (!pairingAltchaToken) {
      toast.error('Please complete verification before generating a pairing key.');
      return;
    }
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      if (!token) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pair-start`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'X-SS-Auth': 'user',
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({
            domainId: domain.id,
            siteUrl: domain.domainName,
            portalBaseUrl: window.location.origin,
            altchaToken: pairingAltchaToken,
          }),
        }
      );
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.error || `pair-start failed: ${res.status}`);
      }
      const data = await res.json();
      
      const pairingCode = data?.pairing_code;
      if (!pairingCode) {
        toast.error('Unable to generate pairing code.');
        return;
      }
      const pairingLink = `${window.location.origin}/portal?pairing_code=${encodeURIComponent(pairingCode)}`;
      setPairingLinks((prev) => ({ ...prev, [domain.id]: { link: pairingLink, createdAt: Date.now() } }));
      setPairingAltchaToken(null);
      try {
        await navigator.clipboard.writeText(pairingLink);
        toast.success('Pairing link copied to clipboard (valid for 10 minutes).');
      } catch {
        const textarea = document.createElement('textarea');
        textarea.value = pairingLink;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        try {
          document.execCommand('copy');
          toast.success('Pairing link copied to clipboard (valid for 10 minutes).');
        } catch {
          toast.error('Unable to copy pairing link. Select and copy manually.');
        } finally {
          textarea.remove();
        }
      }
    } catch (err: unknown) {
      console.error('pair-start failed:', err);
      toast.error(await formatEdgeFunctionError(err, 'Unable to generate pairing link.'));
    }
  };

  const handleCopyPairingKey = async (pairingCode: string) => {
    try {
      await navigator.clipboard.writeText(pairingCode);
      toast.success('Pairing key copied to clipboard.');
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = pairingCode;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand('copy');
        toast.success('Pairing key copied to clipboard.');
      } catch {
        toast.error('Unable to copy pairing key. Select and copy manually.');
      } finally {
        textarea.remove();
      }
    }
  };


  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    accounts?.forEach((account) => {
      account.domains.forEach((d) => {
        if (d.status) set.add(d.status);
      });
    });
    const options = Array.from(set).sort();
    if (alertDomainIds.length > 0) {
      options.push('alerts');
    }
    options.push('disconnected');
    return options;
  }, [accounts, alertDomainIds]);

  const formatStatusLabel = (status: string) => {
    if (status === 'pending') return 'Pending Setup';
    if (status === 'active') return 'Protected';
    if (status === 'trial') return 'Trial Active';
    if (status === 'payment_needed') return 'Payment Needed';
    if (status === 'deleted') return 'Removed site';
    if (status === 'disconnected') return 'Disconnected';
    if (status === 'alerts') return 'Issues requiring action';
    return status.replace(/_/g, ' ');
  };

  const handleRestoreDomain = async (domainId: string) => {
    setRestoringDomainId(domainId);
    try {
      const { data, error } = await supabase.functions.invoke('admin-deleted-domains', {
        body: { action: 'restore', domainId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success('Domain restored successfully');
      refetchDeleted();
      refetch();
    } catch (err: any) {
      if (isUnauthorized(err)) {
        await handleUnauthorized();
        return;
      }
      console.error('Restore domain failed:', err);
      toast.error(err?.message || 'Failed to restore domain');
    } finally {
      setRestoringDomainId(null);
    }
  };

  const handleAdminDeleteDomain = async (domain: AdminDomain, account: AdminAccount) => {
    if (!account.email) {
      toast.error('User email is missing for this account.');
      return;
    }
    if (confirmDomainInput.trim().toLowerCase() !== domain.domainName.toLowerCase()) {
      toast.error('Please type the domain name to confirm.');
      return;
    }

    const paymentIssue = isPaymentNeeded(domain.status);
    setDeletingDomainId(domain.id);
    try {
      let chargeResult: { charged?: boolean; reason?: string; error?: string } | null = null;
      if (!paymentIssue) {
        const { data, error: chargeError } = await supabase.functions.invoke('charge-cancellation-fee', {
          body: {
            domainName: domain.domainName,
            domainId: domain.id,
            subscriptionId: domain.stripeSubscriptionId || undefined,
            userId: account.userId,
          },
        });
        chargeResult = data || null;

        if (chargeError) {
          console.error('Cancellation fee charge error:', chargeError);
          toast.error('Failed to process cancellation fee. Please try again.');
          return;
        }

        if (chargeResult?.error) {
          console.error('Cancellation fee error:', chargeResult.error);
          toast.error('Failed to process cancellation fee: ' + chargeResult.error);
          return;
        }

        if (!chargeResult?.charged) {
          if (chargeResult?.reason === 'free_trial') {
            toast.info('This domain is still in the 7-day free trial. No cancellation fee will be charged.');
          } else if (chargeResult?.reason === 'no_fee') {
            toast.info('This reviewed pricing domain does not have a cancellation fee.');
          } else {
            toast.error('Unable to charge cancellation fee. Please update the payment method.');
            return;
          }
        }
      }

      const { data, error } = await supabase.functions.invoke('admin-deleted-domains', {
        body: { action: 'delete', domainId: domain.id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      await supabase.functions.invoke('notify-domain-change', {
        body: {
          action: 'deleted',
          domains: [
            {
              domainName: domain.domainName,
              status: domain.status || undefined,
              createdAt: domain.createdAt || undefined,
            },
          ],
          userEmail: account.email || '',
          changes: paymentIssue
            ? `Domain "${domain.domainName}" was deleted by an admin while payment was incomplete. No cancellation fee was charged.`
            : chargeResult?.reason === 'free_trial'
              ? `Domain "${domain.domainName}" was deleted by an admin during the 7-day free trial. No cancellation fee was charged.`
              : chargeResult?.reason === 'no_fee'
                ? `Domain "${domain.domainName}" was deleted under reviewed pricing terms. No cancellation fee was charged.`
                : `Domain "${domain.domainName}" was deleted by an admin. Cancellation fee was charged.`,
        },
      });

      toast.success(
        paymentIssue
          ? 'Domain deleted. No cancellation fee due to incomplete payment.'
          : chargeResult?.reason === 'free_trial'
            ? 'Domain deleted during the 7-day free trial. No cancellation fee was charged.'
            : chargeResult?.reason === 'no_fee'
              ? 'Domain deleted. No cancellation fee applies under the reviewed pricing terms.'
              : 'Domain deleted. Cancellation fee charged.',
      );
      setConfirmDomainInput('');
      refetch();
      refetchDeleted();
    } catch (err: any) {
      if (isUnauthorized(err)) {
        await handleUnauthorized();
        return;
      }
      console.error('Delete domain failed:', err);
      toast.error(err?.message || 'Failed to delete domain');
    } finally {
      setDeletingDomainId(null);
    }
  };

  const isUnauthorized = (err: any) => {
    const status = err?.status || err?.error?.status;
    const message = (err?.message || '').toLowerCase();
    return (
      status === 401 ||
      status === 403 ||
      message.includes('unauthorized') ||
      message.includes('forbidden') ||
      message.includes('invalid jwt')
    );
  };

  const handleUnauthorized = async () => {
    toast.error('Session expired or unauthorized. Please sign in again.');
    await signOut();
    navigate('/portal/auth');
  };

  const handlePaymentBypassToggle = async (account: AdminAccount, enabled: boolean) => {
    setUpdatingBypassUserId(account.userId);
    try {
      const { data, error } = await supabase.functions.invoke('admin-set-payment-bypass', {
        body: { userId: account.userId, enabled },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`Payment bypass ${enabled ? 'enabled' : 'disabled'} for ${account.email || account.userId}`);
      refetch();
    } catch (err: any) {
      if (isUnauthorized(err)) {
        await handleUnauthorized();
        return;
      }
      console.error('admin-set-payment-bypass failed:', err);
      toast.error(err?.message || 'Failed to update payment bypass');
    } finally {
      setUpdatingBypassUserId(null);
    }
  };

  const handleDisableMfa = async (account: AdminAccount) => {
    setDisablingMfaUserId(account.userId);
    try {
      const { data, error } = await supabase.functions.invoke('admin-disable-mfa', {
        body: { userId: account.userId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`Portal MFA disabled for ${account.email || account.userId}`);
      refetch();
    } catch (err: any) {
      if (isUnauthorized(err)) {
        await handleUnauthorized();
        return;
      }
      console.error('admin-disable-mfa failed:', err);
      toast.error(err?.message || 'Failed to disable MFA');
    } finally {
      setDisablingMfaUserId(null);
    }
  };

  const resetInviteForm = () => {
    setInviteEmail('');
    setInviteFullName('');
    setInviteAccountKind('standard');
    const activePrices = (inviteSettings?.priceCatalog || []).filter((row) => row.active);
    const defaultPrice = activePrices.find((row) => row.priceCents === 500) || activePrices[0];
    if (defaultPrice) setInviteDevPriceCents(defaultPrice.priceCents);
  };

  const handleInviteAccount = async () => {
    const email = inviteEmail.trim().toLowerCase();
    const fullName = inviteFullName.trim();
    if (!email || !fullName) {
      toast.error('Email and full name are required.');
      return;
    }

    setInvitingAccount(true);
    try {
      const payload: Record<string, unknown> = {
        email,
        fullName,
        accountKind: inviteAccountKind,
      };
      if (inviteAccountKind === 'dev') {
        payload.devPriceCents = inviteDevPriceCents;
      }

      const { data, error } = await supabase.functions.invoke('admin-invite-account', {
        body: payload,
      });

      const errorMessage = data?.error || error?.message;
      if (errorMessage) {
        // Extract readable message from the response context if available
        if (!data?.error && error && typeof (error as any).context?.json === 'function') {
          try {
            const body = await (error as any).context.json();
            if (body?.error) {
              toast.error(body.error);
              return;
            }
          } catch { /* fall through */ }
        }
        toast.error(errorMessage === 'Edge Function returned a non-2xx status code'
          ? 'Failed to send invite.'
          : errorMessage);
        return;
      }

      toast.success(`Invite sent to ${email}.`);
      setInviteDialogOpen(false);
      resetInviteForm();
      refetch();
    } catch (err: unknown) {
      if (isUnauthorized(err)) {
        await handleUnauthorized();
        return;
      }
      console.error('admin-invite-account failed:', err);
      toast.error(await formatEdgeFunctionError(err, 'Failed to send invite.'));
    } finally {
      setInvitingAccount(false);
    }
  };

  const handleResendInvite = async (account: AdminAccount) => {
    const inviteId = account.pendingInvite?.id;
    if (!inviteId) return;

    setResendingInviteId(inviteId);
    try {
      const { data, error } = await supabase.functions.invoke('admin-resend-account-invite', {
        body: { inviteId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`Invite resent to ${account.email || 'user'}.`);
      refetch();
    } catch (err: any) {
      if (isUnauthorized(err)) {
        await handleUnauthorized();
        return;
      }
      console.error('admin-resend-account-invite failed:', err);
      toast.error(err?.message || 'Failed to resend invite.');
    } finally {
      setResendingInviteId(null);
    }
  };

  const handlePortfolioPricingReview = async (
    account: AdminAccount,
    action: 'under_review' | 'approved' | 'declined',
  ) => {
    const application = account.portfolioPricingApplication;
    if (!application) return;

    setReviewingPortfolioApplicationId(application.id);
    try {
      const payload: Record<string, unknown> = {
        applicationId: application.id,
        action,
        reviewNotes: portfolioReviewNotes.trim() || null,
      };

      if (action === 'approved') {
        payload.devPriceCents = portfolioApprovalPriceCents;
        payload.minimumActiveDomains = portfolioApprovalMinimumActiveDomains;
      }

      const { data, error } = await invokeEdgeWithAuthRetry<any>(
        'admin-review-portfolio-pricing-application',
        { body: payload },
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success(
        action === 'approved'
          ? `Portfolio pricing approved for ${account.email || 'user'}.`
          : action === 'declined'
            ? `Portfolio pricing declined for ${account.email || 'user'}.`
            : `Portfolio pricing marked under review for ${account.email || 'user'}.`,
      );

      if (action === 'approved' || action === 'declined') {
        capturePortfolioPricingEvent(
          action === 'approved'
            ? 'portfolio_pricing_application_approved'
            : 'portfolio_pricing_application_declined',
          {
            page: 'admin_panel',
            review_action: action,
            ...buildPortfolioPricingAnalyticsProps({
              submissionSource: application.submissionSource,
              status: action,
              accountKind: action === 'approved' ? 'dev' : 'standard',
              campaignSource: application.signupSource,
              approximatePortfolioSize: application.approximatePortfolioSize,
              currentSiteCount: application.currentSiteCount,
              expectedSiteCount: application.expectedSiteCount,
            }),
          },
        );
      }

      const refreshed = await refetch();
      const nextSelectedAccount =
        refreshed.data?.accounts.find((entry) => entry.userId === account.userId) || null;
      setSelectedAccount(nextSelectedAccount);
    } catch (err: any) {
      if (isUnauthorized(err)) {
        await handleUnauthorized();
        return;
      }
      console.error('admin-review-portfolio-pricing-application failed:', err);
      toast.error(await formatEdgeFunctionError(err, 'Failed to update portfolio pricing review.'));
    } finally {
      setReviewingPortfolioApplicationId(null);
    }
  };

  const handleClearReviewedPricingReassessment = async (account: AdminAccount) => {
    setClearingReviewedPricingReassessmentUserId(account.userId);
    try {
      const { data, error } = await invokeEdgeWithAuthRetry<any>(
        'admin-clear-reviewed-pricing-reassessment',
        { body: { userId: account.userId } },
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success(`Reviewed pricing reassessment cleared for ${account.email || 'user'}.`);
      const refreshed = await refetch();
      const nextSelectedAccount =
        refreshed.data?.accounts.find((entry) => entry.userId === account.userId) || null;
      setSelectedAccount(nextSelectedAccount);
    } catch (err: any) {
      if (isUnauthorized(err)) {
        await handleUnauthorized();
        return;
      }
      console.error('admin-clear-reviewed-pricing-reassessment failed:', err);
      toast.error(await formatEdgeFunctionError(err, 'Failed to clear reviewed pricing reassessment.'));
    } finally {
      setClearingReviewedPricingReassessmentUserId(null);
    }
  };

  const getTestModeInput = (account: AdminAccount) => {
    const fallback = {
      isTestUser: Boolean(account.isTestUser),
      emulationStatus: account.emulatedSubscriptionStatus || 'none',
      graceEnd: account.emulatedGraceEnd || '',
      suspendedSince: account.emulatedSuspendedSince || '',
      confirmPhrase: '',
    };
    return testModeInputs[account.userId] || fallback;
  };

  const updateTestModeInput = (
    account: AdminAccount,
    patch: Partial<{
      isTestUser: boolean;
      emulationStatus: string;
      graceEnd: string;
      suspendedSince: string;
      confirmPhrase: string;
    }>,
  ) => {
    setTestModeInputs((prev) => ({
      ...prev,
      [account.userId]: {
        ...getTestModeInput(account),
        ...(prev[account.userId] || {}),
        ...patch,
      },
    }));
  };

  const handleTestModeUpdate = async (account: AdminAccount) => {
    if (!testModeSettings?.allowed) {
      toast.error('Test mode emulation is disabled in this environment.');
      return;
    }
    const input = getTestModeInput(account);
    setUpdatingTestModeUserId(account.userId);
    try {
      const { data, error } = await supabase.functions.invoke('admin-set-test-mode', {
        body: {
          userId: account.userId,
          isTestUser: input.isTestUser,
          emulationStatus: input.emulationStatus,
          graceEnd: input.graceEnd || null,
          suspendedSince: input.suspendedSince || null,
          confirmPhrase: input.confirmPhrase,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success('Test mode settings updated.');
      updateTestModeInput(account, { confirmPhrase: '' });
      refetch();
    } catch (err: any) {
      if (isUnauthorized(err)) {
        await handleUnauthorized();
        return;
      }
      console.error('admin-set-test-mode failed:', err);
      toast.error(err?.message || 'Failed to update test mode settings');
    } finally {
      setUpdatingTestModeUserId(null);
    }
  };

  const handleTestModeSettingSave = async () => {
    if (!testModeSettings) return;
    setSavingTestModeSetting(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-set-test-mode-settings', {
        body: {
          enabled: testModeSettingEnabled,
          confirmPhrase: testModeSettingConfirm,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success('Test mode setting updated.');
      setTestModeSettingConfirm('');
      refetch();
    } catch (err: any) {
      if (isUnauthorized(err)) {
        await handleUnauthorized();
        return;
      }
      console.error('admin-set-test-mode-settings failed:', err);
      toast.error(err?.message || 'Failed to update test mode setting');
    } finally {
      setSavingTestModeSetting(false);
    }
  };

  const handleAdminGeoGlobalSave = async () => {
    if (!isSuperAdmin) return;
    const ok = window.confirm(
      adminGeoGlobalEnabled
        ? 'Enable global Admin Access Guard enforcement?'
        : 'Disable global Admin Access Guard enforcement?'
    );
    if (!ok) return;
    setSavingAdminGeoGlobal(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-set-admin-geo-settings', {
        body: { enabled: adminGeoGlobalEnabled },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success('Admin Access Guard global flag updated.');
      refetch();
    } catch (err: any) {
      if (isUnauthorized(err)) {
        await handleUnauthorized();
        return;
      }
      console.error('admin-set-admin-geo-settings failed:', err);
      toast.error(err?.message || 'Failed to update Admin Access Guard setting');
    } finally {
      setSavingAdminGeoGlobal(false);
    }
  };

  const handleSecurityControlsSettingSave = async () => {
    if (!isSuperAdmin || !securityControlsSettings) return;
    setSavingSecurityControlsSetting(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-set-security-controls-settings', {
        body: {
          enabled: securityControlsWriteEnabled,
          confirmPhrase: securityControlsConfirm,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success('Security controls portal-write setting updated.');
      setSecurityControlsConfirm('');
      refetch();
    } catch (err: any) {
      if (isUnauthorized(err)) {
        await handleUnauthorized();
        return;
      }
      console.error('admin-set-security-controls-settings failed:', err);
      toast.error(err?.message || 'Failed to update security controls setting');
    } finally {
      setSavingSecurityControlsSetting(false);
    }
  };

  const handleAdminPortalFeatureSave = async (
    setting: 'adminAiAssistantEnabled' | 'adminMfaOutreachEnabled',
    enabled: boolean,
    confirmPhrase: string,
    setSaving: (value: boolean) => void,
    onSuccess: () => void,
  ) => {
    if (!isSuperAdmin || !adminPortalFeatures) return;
    setSaving(true);
    try {
      const { data, error } = await invokeEdgeWithAuthRetry<any>('admin-set-admin-portal-feature-settings', {
        body: {
          setting,
          enabled,
          confirmPhrase,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success('Admin portal feature setting updated.');
      onSuccess();
      refetch();
    } catch (err: any) {
      if (isUnauthorized(err)) {
        await handleUnauthorized();
        return;
      }
      console.error('admin-set-admin-portal-feature-settings failed:', err);
      toast.error(err?.message || 'Failed to update admin portal feature setting');
    } finally {
      setSaving(false);
    }
  };

  const handleAdminAiAssistantSettingSave = async () => {
    await handleAdminPortalFeatureSave(
      'adminAiAssistantEnabled',
      adminAiAssistantEnabled,
      adminAiAssistantConfirm,
      setSavingAdminAiAssistantSetting,
      () => setAdminAiAssistantConfirm(''),
    );
  };

  const handleAdminMfaOutreachSettingSave = async () => {
    await handleAdminPortalFeatureSave(
      'adminMfaOutreachEnabled',
      adminMfaOutreachEnabled,
      adminMfaOutreachConfirm,
      setSavingAdminMfaOutreachSetting,
      () => setAdminMfaOutreachConfirm(''),
    );
  };

  const fetchIncidents = async () => {
    setIncidentsLoading(true);
    try {
      const statusFilters = incidentStatusFilter === 'all' ? undefined : [incidentStatusFilter];
      const severityFilters =
        incidentSeverityFilter === 'all'
          ? undefined
          : incidentSeverityFilter === 'curated'
          ? ['SEV1', 'SEV2']
          : [incidentSeverityFilter];
      const { data, error } = await supabase.functions.invoke('incidents-list', {
        body: {
          status: statusFilters,
          severity: severityFilters,
          domain: incidentSearch || undefined,
          assignedTo: incidentAssigneeFilter || undefined,
          limit: 100,
          offset: 0,
          sort: 'updated_at',
          direction: 'desc',
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setIncidentList(data?.incidents || []);
      setIncidentCounts({
        openSev1: data?.counts?.openSev1 ?? 0,
        openSev2: data?.counts?.openSev2 ?? 0,
        openSev3: data?.counts?.openSev3 ?? 0,
        totalOpen: data?.counts?.totalOpen ?? 0,
      });
      if (incidentsDebug) {
        setIncidentDebugInfo({
          filters: {
            status: statusFilters ?? null,
            severity: severityFilters ?? null,
            domain: incidentSearch || null,
            assignedTo: incidentAssigneeFilter || null,
            sort: 'updated_at',
            direction: 'desc',
          },
          resultCount: (data?.incidents || []).length,
          lastRefreshedAt: new Date().toISOString(),
        });
      }
    } catch (err: any) {
      if (isUnauthorized(err)) {
        await handleUnauthorized();
        return;
      }
      console.error('incidents-list failed:', err);
      toast.error(err?.message || 'Failed to load incidents');
    } finally {
      if (incidentsDebug && !incidentDebugInfo) {
        setIncidentDebugInfo({
          filters: {
            status: incidentStatusFilter === 'all' ? null : [incidentStatusFilter],
            severity:
              incidentSeverityFilter === 'all'
                ? null
                : incidentSeverityFilter === 'curated'
                ? ['SEV1', 'SEV2']
                : [incidentSeverityFilter],
            domain: incidentSearch || null,
            assignedTo: incidentAssigneeFilter || null,
            sort: 'updated_at',
            direction: 'desc',
          },
          resultCount: 0,
          lastRefreshedAt: new Date().toISOString(),
        });
      }
      setIncidentsLoading(false);
    }
  };

  const fetchIncidentDetail = async (incidentId: string) => {
    setIncidentDetailLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('incidents-get', {
        body: { incidentId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setSelectedIncident(data?.incident || null);
      setIncidentEvents(data?.events || []);
      setIncidentNote('');
      setIncidentResolveReason('');
      setIncidentResolveType('RESOLVED');
      setIncidentAssignTo(data?.incident?.assigned_to || '');
      setIncidentUpdateSeverity('');
      setIncidentUpdateSignal('');
    } catch (err: any) {
      if (isUnauthorized(err)) {
        await handleUnauthorized();
        return;
      }
      console.error('incidents-get failed:', err);
      toast.error(err?.message || 'Failed to load incident');
    } finally {
      setIncidentDetailLoading(false);
    }
  };

  const handleIncidentAck = async () => {
    if (!selectedIncident) return;
    setIncidentActionLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('incidents-ack', {
        body: {
          incidentId: selectedIncident.id,
          expectedUpdatedAt: selectedIncident.updated_at,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success('Incident acknowledged.');
      await fetchIncidentDetail(selectedIncident.id);
      await fetchIncidents();
    } catch (err: any) {
      if (isUnauthorized(err)) {
        await handleUnauthorized();
        return;
      }
      console.error('incidents-ack failed:', err);
      toast.error(err?.message || 'Failed to acknowledge incident');
    } finally {
      setIncidentActionLoading(false);
    }
  };

  const handleIncidentAssign = async () => {
    if (!selectedIncident || !incidentAssignTo) return;
    setIncidentActionLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('incidents-assign', {
        body: {
          incidentId: selectedIncident.id,
          assignedTo: incidentAssignTo,
          expectedUpdatedAt: selectedIncident.updated_at,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success('Incident assigned.');
      await fetchIncidentDetail(selectedIncident.id);
      await fetchIncidents();
    } catch (err: any) {
      if (isUnauthorized(err)) {
        await handleUnauthorized();
        return;
      }
      console.error('incidents-assign failed:', err);
      toast.error(err?.message || 'Failed to assign incident');
    } finally {
      setIncidentActionLoading(false);
    }
  };

  const handleIncidentUpdate = async () => {
    if (!selectedIncident) return;
    setIncidentActionLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('incidents-update', {
        body: {
          incidentId: selectedIncident.id,
          expectedUpdatedAt: selectedIncident.updated_at,
          patch: {
            severity: incidentUpdateSeverity || undefined,
            signalSummary: incidentUpdateSignal || undefined,
          },
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (data?.updated) {
        toast.success('Incident updated.');
        await fetchIncidentDetail(selectedIncident.id);
        await fetchIncidents();
      } else {
        toast.message('No changes to update.');
      }
    } catch (err: any) {
      if (isUnauthorized(err)) {
        await handleUnauthorized();
        return;
      }
      console.error('incidents-update failed:', err);
      toast.error(err?.message || 'Failed to update incident');
    } finally {
      setIncidentActionLoading(false);
    }
  };

  const handleIncidentNote = async () => {
    if (!selectedIncident || !incidentNote.trim()) return;
    setIncidentActionLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('incidents-note', {
        body: {
          incidentId: selectedIncident.id,
          message: incidentNote.trim(),
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success('Note added.');
      await fetchIncidentDetail(selectedIncident.id);
      setIncidentNote('');
    } catch (err: any) {
      if (isUnauthorized(err)) {
        await handleUnauthorized();
        return;
      }
      console.error('incidents-note failed:', err);
      toast.error(err?.message || 'Failed to add note');
    } finally {
      setIncidentActionLoading(false);
    }
  };

  const handleIncidentResolve = async () => {
    if (!selectedIncident || !incidentResolveReason.trim()) return;
    setIncidentActionLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('incidents-resolve', {
        body: {
          incidentId: selectedIncident.id,
          resolutionReason: incidentResolveReason.trim(),
          resolutionType: incidentResolveType,
          expectedUpdatedAt: selectedIncident.updated_at,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success('Incident resolved.');
      await fetchIncidentDetail(selectedIncident.id);
      await fetchIncidents();
    } catch (err: any) {
      if (isUnauthorized(err)) {
        await handleUnauthorized();
        return;
      }
      console.error('incidents-resolve failed:', err);
      toast.error(err?.message || 'Failed to resolve incident');
    } finally {
      setIncidentActionLoading(false);
    }
  };

  const handleIncidentReopen = async () => {
    if (!selectedIncident) return;
    setIncidentActionLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('incidents-reopen', {
        body: {
          incidentId: selectedIncident.id,
          message: 'Reopened by admin',
          expectedUpdatedAt: selectedIncident.updated_at,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success('Incident reopened.');
      await fetchIncidentDetail(selectedIncident.id);
      await fetchIncidents();
    } catch (err: any) {
      if (isUnauthorized(err)) {
        await handleUnauthorized();
        return;
      }
      console.error('incidents-reopen failed:', err);
      toast.error(err?.message || 'Failed to reopen incident');
    } finally {
      setIncidentActionLoading(false);
    }
  };

  if (checking || loading || isAdminLoading || isSuperAdminLoading || isAdmin === undefined) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading admin tools...
        </div>
      </div>
    );
  }

  // Defensive guard: never render admin data if role check fails
  if (!user || isAdmin === false) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="cyber-card max-w-md mx-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Restricted Admin Access
            </CardTitle>
            <CardDescription>Admin privileges are required to view this area.</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-end">
            <Button onClick={() => navigate('/portal')}>Back to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const panelContent = (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-mono font-bold">Admin Console</h1>
          <p className="text-sm text-muted-foreground">Internal operations view for customer accounts and sites.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'users', label: 'Users' },
            { key: 'sites', label: 'Sites' },
            { key: 'incidents', label: 'Incidents' },
            ...(isSuperAdmin ? [{ key: 'tools', label: 'Admin Tools' }] : []),
          ].map((tab) => (
            <Button
              key={tab.key}
              variant="outline"
              size="sm"
              className={adminTab === tab.key ? 'border-[hsl(var(--ss-info))] text-[hsl(var(--ss-info))] bg-[var(--ss-info-bg)]' : undefined}
              onClick={() => setAdminTab(tab.key as 'overview' | 'users' | 'sites' | 'incidents' | 'tools')}
            >
              {tab.label}
            </Button>
          ))}
          {testModeSettings?.enabled && (
            <Badge variant="warning">
              Test Mode Enabled
            </Badge>
          )}
        </div>
      </div>

      {adminTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid gap-3 md:grid-cols-5">
            <Card className="border-border/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Sites Connected</CardTitle>
                <CardDescription>Heartbeat received</CardDescription>
              </CardHeader>
              <CardContent className="text-2xl font-mono font-bold text-[hsl(var(--ss-success))]">
                {connectedCount}
              </CardContent>
            </Card>
            <Card className="border-border/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Sites with Errors</CardTitle>
                <CardDescription>Missing heartbeat</CardDescription>
              </CardHeader>
              <CardContent className="text-2xl font-mono font-bold text-[hsl(var(--ss-warning))]">
                {errorCount}
              </CardContent>
            </Card>
            <Card className="border-border/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Awaiting Connection</CardTitle>
                <CardDescription>No heartbeat yet</CardDescription>
              </CardHeader>
              <CardContent className="text-2xl font-mono font-bold text-[hsl(var(--ss-warning))]">
                {provisioningCount}
              </CardContent>
            </Card>
            <Card className="border-border/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Issues Requiring Action</CardTitle>
                <CardDescription>Triaged signal</CardDescription>
              </CardHeader>
              <CardContent className="text-2xl font-mono font-bold text-[hsl(var(--ss-danger))]">
                {issuesCount}
              </CardContent>
            </Card>
            <Card className="border-border/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Subscription Issues</CardTitle>
                <CardDescription>Past due or paused</CardDescription>
              </CardHeader>
              <CardContent className="text-2xl font-mono font-bold text-[hsl(var(--ss-warning))]">
                {subscriptionIssueCount}
              </CardContent>
            </Card>
          </div>

          <Card className="border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Needs attention</CardTitle>
              <CardDescription>Top items requiring action</CardDescription>
            </CardHeader>
            <CardContent>
              {needsAttention.length === 0 ? (
                <p className="text-sm text-muted-foreground">No urgent items detected.</p>
              ) : (
                <div className="space-y-3">
                  {needsAttention.map((item) => (
                    <div
                      key={item.id}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/50 bg-muted/20 px-3 py-2 text-sm"
                    >
                      <div className="space-y-1">
                        <div className="font-mono">{item.domain.domainName}</div>
                        <div className="text-xs text-muted-foreground">
                          {item.issue} · Last check-in {item.lastCheckIn}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (item.actionType === 'user') {
                            setSelectedAccount(item.account);
                            setAdminTab('users');
                          } else {
                            setDiagnosticsDomain({
                              domain: item.domain,
                              paymentBypass: Boolean(item.account.paymentLockBypass),
                              managedSite:
                                managedSiteByDomainId.byDomainId.get(item.domain.id) ||
                                managedSiteByDomainId.bySiteUrl.get(normalizeDomainValue(item.domain.domainName) || ''),
                            });
                            setAdminTab('sites');
                          }
                        }}
                      >
                        {item.action}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {adminTab === 'users' && (
        <div className="space-y-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col sm:flex-row flex-1 items-stretch sm:items-center gap-2">
              <div className="relative flex-1">
                <Input
                  placeholder="Search by email, name, domain, or URL"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-10"
                />
                <User className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
              <select
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                className="px-2 py-2 rounded-md border border-border/50 bg-background/70 text-sm w-full sm:w-[190px]"
              >
                <option value="all">All users</option>
                <option value="past_due">Past due</option>
                <option value="suspended">Suspended</option>
                <option value="payment_bypass">Payment bypass</option>
                <option value="test_users">Test users</option>
                <option value="deleted">Removed sites</option>
              </select>
            </div>
            <div className="flex w-full sm:w-auto gap-2">
              {inviteSettings?.enabled && (
                <Button
                  className="w-full sm:w-auto"
                  onClick={() => setInviteDialogOpen(true)}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Invite account
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => { setUserSearch(''); refetch(); }}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          <Card className="border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Users</CardTitle>
              <CardDescription>Manage customer accounts and access flags.</CardDescription>
            </CardHeader>
            <CardContent>
              {accountsLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading accounts...
                </div>
              ) : filteredAccounts.length === 0 ? (
                <div className="text-muted-foreground text-sm">No accounts found.</div>
              ) : (
                <div className="space-y-3">
                  {filteredAccounts.map((account) => {
                    const hasPastDue = account.domains.some((d) => isPaymentNeeded(d.status));
                    const hasSuspended = account.domains.some((d) => d.status === 'cancelled');
                    const statusLabel = account.isTestUser
                      ? 'Test'
                      : hasPastDue
                        ? 'Past due'
                        : hasSuspended
                          ? 'Suspended'
                          : 'Active';

                    return (
                      <div
                        key={account.userId}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/50 bg-muted/10 px-3 py-2 text-sm"
                      >
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span>{account.email || 'Unknown email'}</span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {account.fullName || 'Name not set'} · {account.domains.length} site(s)
                            {account.pendingInvite?.expiresAt
                              ? ` · invite expires ${new Date(account.pendingInvite.expiresAt).toLocaleString()}`
                              : ''}
                          </div>
                          {account.portfolioPricingApplication && (
                            <div className="text-xs text-muted-foreground">
                              Portfolio pricing {PORTFOLIO_PRICING_STATUS_LABELS[account.portfolioPricingApplication.status].toLowerCase()}
                              {account.portfolioPricingApplication.submissionSource
                                ? ` · submitted via ${account.portfolioPricingApplication.submissionSource}`
                                : ''}
                              {account.portfolioPricingApplication.historyCount > 1
                                ? ` · ${account.portfolioPricingApplication.historyCount} total submissions`
                                : ''}
                            </div>
                          )}
                          {account.accountKind === 'dev' && typeof account.reviewedPricingMinActiveDomains === 'number' && (
                            <div className="text-xs text-muted-foreground">
                              Reviewed pricing threshold {account.reviewedPricingMinActiveDomains} active paid domain{account.reviewedPricingMinActiveDomains === 1 ? '' : 's'}
                              {typeof account.activePaidReviewedPricingDomains === 'number'
                                ? ` · currently ${account.activePaidReviewedPricingDomains}`
                                : ''}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline">{statusLabel}</Badge>
                          {account.accountKind === 'dev' && (
                            <Badge variant="secondary">Dev</Badge>
                          )}
                          {account.reviewedPricingReassessmentNeeded && (
                            <Badge variant="destructive">Reassess pricing</Badge>
                          )}
                          {account.pendingInvite && (
                            <Badge variant="secondary">
                              Invite {account.pendingInvite.status}
                            </Badge>
                          )}
                          {account.portfolioPricingApplication && (
                            <Badge variant={getPortfolioPricingStatusTone(account.portfolioPricingApplication.status)}>
                              {PORTFOLIO_PRICING_STATUS_LABELS[account.portfolioPricingApplication.status]}
                            </Badge>
                          )}
                          {account.mfaMethod && (
                            <Badge variant="secondary">MFA</Badge>
                          )}
                          {account.paymentLockBypass && (
                            <Badge variant="secondary">Bypass</Badge>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm">
                                <MoreHorizontal className="h-4 w-4 mr-2" />
                                Actions
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setSelectedAccount(account)}>
                                View details
                              </DropdownMenuItem>
                              {inviteSettings?.enabled && account.pendingInvite && (
                                <DropdownMenuItem
                                  onClick={() => handleResendInvite(account)}
                                  disabled={resendingInviteId === account.pendingInvite.id}
                                >
                                  {resendingInviteId === account.pendingInvite.id ? 'Resending…' : 'Resend invite'}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => handleImpersonate(account)}>
                                View customer dashboard
                              </DropdownMenuItem>
                              {account.mfaMethod && (
                                <DropdownMenuItem onClick={() => handleDisableMfa(account)}>
                                  Disable MFA
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => handlePaymentBypassToggle(account, !account.paymentLockBypass)}
                              >
                                {account.paymentLockBypass ? 'Disable payment bypass' : 'Enable payment bypass'}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {adminTab === 'sites' && (
        <div className="space-y-6">
          <Card className="border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Security Health by Domain</CardTitle>
              <CardDescription>Latest coverage and posture scores across all domains.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-1 flex-col sm:flex-row gap-2">
                  <Input
                    placeholder="Filter by domain"
                    value={postureSearch}
                    onChange={(e) => setPostureSearch(e.target.value)}
                    className="w-full sm:w-[240px]"
                  />
                  <select
                    value={postureFilter}
                    onChange={(e) => setPostureFilter(e.target.value as 'all' | 'low' | 'medium' | 'high' | 'missing')}
                    className="px-2 py-2 rounded-md border border-border/50 bg-background/70 text-sm"
                  >
                    <option value="all">All coverage states</option>
                    <option value="low">Action required</option>
                    <option value="medium">{STATUS_LABELS.needsHost}</option>
                    <option value="high">Covered</option>
                    <option value="missing">Monitoring planned</option>
                  </select>
                  <select
                    value={postureSort}
                    onChange={(e) => setPostureSort(e.target.value as 'coverage_desc' | 'coverage_asc' | 'posture_desc' | 'posture_asc' | 'updated_desc')}
                    className="px-2 py-2 rounded-md border border-border/50 bg-background/70 text-sm"
                  >
                    <option value="coverage_desc">Sort: Coverage high → low</option>
                    <option value="coverage_asc">Sort: Coverage low → high</option>
                    <option value="posture_desc">Sort: Posture high → low</option>
                    <option value="posture_asc">Sort: Posture low → high</option>
                    <option value="updated_desc">Sort: Recently updated</option>
                  </select>
                </div>
              </div>

              {postureLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading security health…
                </div>
              ) : filteredPostureDomains.length === 0 ? (
                <div className="text-sm text-muted-foreground">No domains match the current filters.</div>
              ) : (
                <div className="space-y-3">
                  {filteredPostureDomains.map((domain) => {
                    const coverageTone = getScoreTone(domain.coverage_score);
                    const postureTone = getScoreTone(domain.posture_score);
                    const coverageDrivers = getTopDrivers(domain.coverage_breakdown);
                    const postureDrivers = getTopDrivers(domain.posture_breakdown);
                    return (
                      <div
                        key={domain.domain_id}
                        className="rounded-md border border-border/50 bg-muted/10 px-3 py-3 text-sm space-y-2"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <div className="font-mono text-sm">{domain.domain_name}</div>
                            <div className="text-xs text-muted-foreground">
                              Last updated {domain.computed_at ? new Date(domain.computed_at).toLocaleString() : 'Not reported'}
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant={coverageTone === 'danger' ? 'danger' : coverageTone === 'warning' ? 'warning' : coverageTone === 'success' ? 'success' : 'neutral'}>
                              Coverage {domain.coverage_score ?? '-'}
                            </Badge>
                            <Badge variant={postureTone === 'danger' ? 'danger' : postureTone === 'warning' ? 'warning' : postureTone === 'success' ? 'success' : 'neutral'}>
                              Posture {domain.posture_score ?? '-'}
                            </Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/portal/domains/${domain.domain_id}/coverage`)}
                            >
                              Coverage
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/portal/domains/${domain.domain_id}/logs`)}
                            >
                              Activity
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/portal/domains/${domain.domain_id}/evidence`)}
                            >
                              Evidence
                            </Button>
                          </div>
                        </div>
                        <div className="grid gap-2 md:grid-cols-2 text-xs text-muted-foreground">
                          <div>
                            <div className="font-semibold text-foreground">Top coverage drivers</div>
                            {coverageDrivers.length === 0 ? (
                              <div>No breakdown reported.</div>
                            ) : (
                              <ul className="list-disc list-inside">
                                {coverageDrivers.map((driver) => (
                                  <li key={driver.label}>{driver.label}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <div>
                            <div className="font-semibold text-foreground">Top posture drivers</div>
                            {postureDrivers.length === 0 ? (
                              <div>No breakdown reported.</div>
                            ) : (
                              <ul className="list-disc list-inside">
                                {postureDrivers.map((driver) => (
                                  <li key={driver.label}>{driver.label}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col sm:flex-row flex-1 items-stretch sm:items-center gap-2">
              <div className="relative flex-1">
                <Input
                  placeholder="Search by domain, email, or URL"
                  value={siteSearch}
                  onChange={(e) => setSiteSearch(e.target.value)}
                  className="pl-10"
                />
                <Globe className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => {
                  const val = e.target.value;
                  setStatusFilter(val);
                  onStatusFilterChange?.(val);
                }}
                className="px-2 py-2 rounded-md border border-border/50 bg-background/70 text-sm w-full sm:w-[190px]"
              >
                <option value="all">All</option>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {formatStatusLabel(status)}
                  </option>
                ))}
              </select>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full sm:w-auto">
                  <MoreHorizontal className="h-4 w-4 mr-2" />
                  Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleBulkRefresh}>
                  Refresh status
                </DropdownMenuItem>
                <DropdownMenuItem onClick={exportSitesList}>
                  Export list
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowDeleted((prev) => !prev)}>
                  {showDeleted ? 'Hide removed' : 'Show removed'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <Card className="border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Sites</CardTitle>
              <CardDescription>Connection and protection status across domains.</CardDescription>
            </CardHeader>
            <CardContent>
              {filteredSites.length === 0 ? (
                <div className="text-muted-foreground text-sm">No sites found.</div>
              ) : (
                <div className="space-y-3">
                  {filteredSites.map(({ domain, account, managedSite }) => {
                    const heartbeat = getHeartbeatSummary(managedSite?.last_heartbeat_at);
                    const installTracking = resolveHostedPluginInstall(managedSite);
                    const automationTracking = resolveHostedAutomation(managedSite);
                    const effective = resolveEffectiveSubscription({
                      domainStatus: domain.status,
                      isTestUser: account.isTestUser,
                      emulationStatus: account.emulatedSubscriptionStatus,
                      graceEnd: account.emulatedGraceEnd || null,
                      suspendedSince: account.emulatedSuspendedSince || null,
                    });
                    const billingLabel = effective.status === 'active'
                      ? 'Active'
                      : effective.status === 'past_due'
                        ? 'Past due'
                        : effective.status === 'trial'
                          ? 'Trial active'
                          : effective.status === 'suspended' || effective.status === 'cancelled'
                            ? 'Suspended'
                            : 'Paused';
                    const protectionLabel = effective.status === 'active'
                      ? 'Live'
                      : effective.status === 'past_due'
                        ? 'Live (no updates)'
                        : effective.status === 'trial'
                          ? 'Live during trial'
                          : 'Paused';
                    const issueCount = alertDomainSet.has(domain.id) ? 1 : 0;
                    const billingVariant = effective.status === 'active'
                      ? 'success'
                      : effective.status === 'past_due'
                        ? 'warning'
                        : effective.status === 'trial'
                          ? 'info'
                          : effective.status === 'suspended' || effective.status === 'cancelled'
                            ? 'danger'
                            : 'neutral';
                    const protectionVariant = effective.status === 'active'
                      ? 'success'
                      : effective.status === 'past_due'
                        ? 'warning'
                        : effective.status === 'trial'
                          ? 'info'
                          : 'danger';

                    return (
                      <div
                        key={domain.id}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/50 bg-muted/10 px-3 py-2 text-sm"
                      >
                        <div className="space-y-1">
                          <div className="font-mono">{domain.domainName}</div>
                          <div className="text-xs text-muted-foreground">
                            {account.email || 'Unknown user'} · Last check-in {managedSite?.last_heartbeat_at ? new Date(managedSite.last_heartbeat_at).toLocaleString() : 'Never'}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant={heartbeat.variant} className="text-xs">
                            Connection: {heartbeat.label}
                          </Badge>
                          <Badge variant={billingVariant} className="text-xs">
                            Billing: {billingLabel}
                          </Badge>
                          <Badge variant={protectionVariant} className="text-xs">
                            Protection: {protectionLabel}
                          </Badge>
                          {installTracking.visibleInPortal && (
                            <Badge
                              variant={
                                installTracking.tone === 'success'
                                  ? 'success'
                                  : installTracking.tone === 'warning'
                                    ? 'warning'
                                    : installTracking.tone === 'danger'
                                      ? 'danger'
                                      : 'neutral'
                              }
                              className="text-xs"
                            >
                              Install: {installTracking.label}
                            </Badge>
                          )}
                          {automationTracking.visibleInPortal && (
                            <Badge
                              variant={
                                automationTracking.tone === 'success'
                                  ? 'success'
                                  : automationTracking.tone === 'warning'
                                    ? 'warning'
                                    : automationTracking.tone === 'danger'
                                      ? 'danger'
                                      : 'neutral'
                              }
                              className="text-xs"
                            >
                              Automation: {automationTracking.label}
                            </Badge>
                          )}
                          <Badge variant={issueCount > 0 ? 'danger' : 'neutral'} className="text-xs">
                            {issueCount} issue{issueCount === 1 ? '' : 's'}
                          </Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDiagnosticsDomain({
                              domain,
                              paymentBypass: Boolean(account.paymentLockBypass),
                              managedSite,
                            })}
                          >
                            <Activity className="h-4 w-4 mr-2" />
                            Diagnostics
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {showDeleted && (
            <Card className="border-border/60">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Trash2 className="h-5 w-5 text-destructive" />
                  <CardTitle>Removed sites</CardTitle>
                </div>
                <CardDescription>
                  View and restore removed sites. Restoring sets status to "pending".
                </CardDescription>
              </CardHeader>
              <CardContent>
                {deletedLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading deleted domains...
                  </div>
                ) : !deletedDomains || deletedDomains.length === 0 ? (
                  <div className="text-muted-foreground text-sm">No deleted domains found.</div>
                ) : (
                  <div className="space-y-3">
                    {deletedDomains.map((domain) => (
                      <div
                        key={domain.id}
                        className="p-4 border border-destructive/30 rounded-lg bg-destructive/5"
                      >
                        <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Globe className="h-4 w-4 text-muted-foreground" />
                            <span className="font-mono text-sm">{domain.domainName}</span>
                            {domain.tier && (
                              <Badge variant={domain.tier === 'premium' ? 'default' : 'secondary'}>
                                {domain.tier === 'premium' ? 'Premium' : domain.tier === 'dev' ? 'Dev' : domain.tier === 'basic' ? 'Basic' : 'Essential'}
                              </Badge>
                            )}
                            <Badge variant="destructive">Removed</Badge>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRestoreDomain(domain.id)}
                            disabled={restoringDomainId === domain.id}
                          >
                            {restoringDomainId === domain.id ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <RotateCcw className="h-4 w-4 mr-2" />
                            )}
                            Restore
                          </Button>
                        </div>
                        <div className="grid gap-2 md:grid-cols-3 text-sm text-muted-foreground">
                          <div>
                            <span className="text-xs">Owner:</span>{' '}
                            <span className="text-foreground">{domain.userEmail || 'Unknown'}</span>
                          </div>
                          <div>
                            <span className="text-xs">Removed:</span>{' '}
                            <span className="text-foreground">
                              {domain.deletedAt ? new Date(domain.deletedAt).toLocaleString() : 'Unknown'}
                            </span>
                          </div>
                          <div>
                            <span className="text-xs">Created:</span>{' '}
                            <span className="text-foreground">
                              {domain.createdAt ? new Date(domain.createdAt).toLocaleDateString() : 'Unknown'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {adminTab === 'incidents' && (
        <div className="space-y-6">
          <Card className="border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Incident overview</CardTitle>
              <CardDescription>Curated incidents requiring human triage.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 md:grid-cols-4">
                <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2">
                  <div className="text-xs text-muted-foreground">Open SEV1</div>
                  <div className="text-lg font-mono text-[hsl(var(--ss-danger))]">{incidentCounts.openSev1}</div>
                </div>
                <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2">
                  <div className="text-xs text-muted-foreground">Open SEV2</div>
                  <div className="text-lg font-mono text-[hsl(var(--ss-warning))]">{incidentCounts.openSev2}</div>
                </div>
                {testModeSettings?.enabled ? (
                  <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2">
                    <div className="text-xs text-muted-foreground">Open SEV3</div>
                    <div className="text-lg font-mono">{incidentCounts.openSev3}</div>
                  </div>
                ) : null}
                <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2">
                  <div className="text-xs text-muted-foreground">Total open</div>
                  <div className="text-lg font-mono">{incidentCounts.totalOpen}</div>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Search</Label>
                  <Input
                    value={incidentSearch}
                    onChange={(event) => setIncidentSearch(event.target.value)}
                    placeholder="Domain or summary"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <select
                    className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
                    value={incidentStatusFilter}
                    onChange={(event) => setIncidentStatusFilter(event.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="open">Open</option>
                    <option value="acknowledged">Acknowledged</option>
                    <option value="in_progress">In progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="false_positive">False positive</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Severity</Label>
                  <select
                    className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
                    value={incidentSeverityFilter}
                    onChange={(event) => setIncidentSeverityFilter(event.target.value)}
                  >
                    <option value="curated">Curated (SEV1/SEV2)</option>
                    {testModeSettings?.enabled ? <option value="all">All severities</option> : null}
                    <option value="SEV1">SEV1</option>
                    <option value="SEV2">SEV2</option>
                    <option value="SEV3">SEV3</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Assigned to</Label>
                  <Input
                    value={incidentAssigneeFilter}
                    onChange={(event) => setIncidentAssigneeFilter(event.target.value)}
                    placeholder="Admin email"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Active incidents</CardTitle>
              <CardDescription>Newest updates first.</CardDescription>
            </CardHeader>
            <CardContent>
              {incidentTarget && incidentHeartbeatStale ? (
                <div className="mb-3 rounded-md border border-border/60 bg-muted/10 px-3 py-2 text-xs text-muted-foreground">
                  Site not connected - incidents may not appear. Last check-in:{' '}
                  <span className="text-foreground">
                    {incidentTarget.managedSite?.last_heartbeat_at
                      ? new Date(incidentTarget.managedSite.last_heartbeat_at).toLocaleString()
                      : 'Never'}
                  </span>
                </div>
              ) : null}
              {incidentsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading incidents...
                </div>
              ) : incidentList.length === 0 ? (
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div>No incidents found.</div>
                  {incidentsDebug && incidentDebugInfo ? (
                    <div className="rounded-md border border-border/60 bg-muted/10 px-3 py-2 text-xs text-muted-foreground">
                      <div className="font-medium text-foreground">Incident query debug</div>
                      <div>Filters: status={incidentDebugInfo.filters.status?.join(',') || 'all'}, severity={incidentDebugInfo.filters.severity?.join(',') || 'all'}, domain={incidentDebugInfo.filters.domain || '-'}, assignedTo={incidentDebugInfo.filters.assignedTo || '-'}</div>
                      <div>Sort: {incidentDebugInfo.filters.sort} {incidentDebugInfo.filters.direction}</div>
                      <div>Returned: {incidentDebugInfo.resultCount} · Last refreshed: {new Date(incidentDebugInfo.lastRefreshedAt).toLocaleString()}</div>
                      <div className="mt-1 text-muted-foreground">
                        If expected incidents are missing, confirm tenant/site/domain mapping, RLS access, and filter defaults.
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-3">
                  {incidentList.map((incident) => {
                    const severityBadge =
                      incident.severity === 'SEV1' ? 'destructive' : incident.severity === 'SEV2' ? 'warning' : 'secondary';
                    const statusBadge =
                      incident.status === 'open'
                        ? 'warning'
                        : incident.status === 'acknowledged'
                        ? 'secondary'
                        : incident.status === 'in_progress'
                        ? 'default'
                        : 'secondary';
                    return (
                      <div
                        key={incident.id}
                        className="flex flex-col gap-3 rounded-md border border-border/60 bg-muted/10 px-3 py-3 text-sm md:flex-row md:items-center md:justify-between"
                      >
                        <div className="space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant={severityBadge as any}>{incident.severity}</Badge>
                            <Badge variant={statusBadge as any}>{incident.status.replace('_', ' ')}</Badge>
                            <span className="font-mono">{incident.domain_name || 'Unknown domain'}</span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {incident.signal_summary || incident.signal_type}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="text-xs text-muted-foreground">
                            Assigned: <span className="text-foreground">{incident.assigned_to || 'Unassigned'}</span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Updated: <span className="text-foreground">{new Date(incident.updated_at).toLocaleString()}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedIncident(incident as IncidentDetail);
                                fetchIncidentDetail(incident.id);
                              }}
                            >
                              View
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {adminTab === 'tools' && isSuperAdmin && (
        <div className="space-y-6">
          <Card className="border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Admin Access Guard (Global)</CardTitle>
              <CardDescription>Internal rollout kill switch.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={adminGeoGlobalEnabled}
                    onCheckedChange={(checked) => setAdminGeoGlobalEnabled(checked)}
                    disabled={savingAdminGeoGlobal}
                  />
                  <span className="text-sm text-muted-foreground">
                    Enable Admin Access Guard enforcement (global)
                  </span>
                </div>
                <Badge variant={adminGeoGlobalEnabled ? 'default' : 'secondary'}>
                  {adminGeoGlobalEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground">
                This controls whether enforcement can activate at all. Keep disabled until rollout is approved.
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={handleAdminGeoGlobalSave} disabled={savingAdminGeoGlobal}>
                  {savingAdminGeoGlobal ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save setting'}
                </Button>
                <span className="text-xs text-muted-foreground">
                  Visible only to super-admins.
                </span>
              </div>
            </CardContent>
          </Card>

          {securityControlsSettings && (
            <Card className="border-border/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Security Controls (Portal Writes)</CardTitle>
                <CardDescription>Controls customer portal write access for security controls pages.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={securityControlsWriteEnabled}
                      onCheckedChange={(checked) => setSecurityControlsWriteEnabled(checked)}
                      disabled={savingSecurityControlsSetting}
                    />
                    <span className="text-sm text-muted-foreground">
                      Enable customer portal writes for security controls
                    </span>
                  </div>
                  <Badge variant={securityControlsWriteEnabled ? 'default' : 'secondary'}>
                    {securityControlsWriteEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  When disabled, customer domain settings remain read-only while rollout validation is in progress.
                </div>
                {securityControlsSettings.requiresConfirmation && securityControlsWriteEnabled && (
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Confirm (type “ENABLE SECURITY CONTROLS WRITES”)
                    </Label>
                    <Input
                      value={securityControlsConfirm}
                      onChange={(event) => setSecurityControlsConfirm(event.target.value)}
                      placeholder="ENABLE SECURITY CONTROLS WRITES"
                    />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={handleSecurityControlsSettingSave}
                    disabled={savingSecurityControlsSetting}
                  >
                    {savingSecurityControlsSetting ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save setting'}
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    Visible only to super-admins.
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {adminPortalFeatures && (
            <Card className="border-border/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Admin AI Assistant</CardTitle>
                <CardDescription>Controls the read-only AI assistant widget in the Admin Portal.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={adminAiAssistantEnabled}
                      onCheckedChange={(checked) => setAdminAiAssistantEnabled(checked)}
                      disabled={savingAdminAiAssistantSetting}
                    />
                    <span className="text-sm text-muted-foreground">
                      Enable Admin AI assistant widget
                    </span>
                  </div>
                  <Badge variant={adminAiAssistantEnabled ? 'default' : 'secondary'}>
                    {adminAiAssistantEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  Read-only guidance only. No automated writes or enforcement actions.
                </div>
                {adminPortalFeatures.requiresConfirmation && adminAiAssistantEnabled && (
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Confirm (type “ENABLE ADMIN AI ASSISTANT”)
                    </Label>
                    <Input
                      value={adminAiAssistantConfirm}
                      onChange={(event) => setAdminAiAssistantConfirm(event.target.value)}
                      placeholder="ENABLE ADMIN AI ASSISTANT"
                    />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={handleAdminAiAssistantSettingSave}
                    disabled={savingAdminAiAssistantSetting}
                  >
                    {savingAdminAiAssistantSetting ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save setting'}
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    Visible only to super-admins.
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {adminPortalFeatures && (
            <Card className="border-border/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">WordPress MFA Outreach</CardTitle>
                <CardDescription>Controls admin bulk reminder emails for connector-reported non-MFA users.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={adminMfaOutreachEnabled}
                      onCheckedChange={(checked) => setAdminMfaOutreachEnabled(checked)}
                      disabled={savingAdminMfaOutreachSetting}
                    />
                    <span className="text-sm text-muted-foreground">
                      Enable Admin MFA outreach workflow
                    </span>
                  </div>
                  <Badge variant={adminMfaOutreachEnabled ? 'default' : 'secondary'}>
                    {adminMfaOutreachEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  Uses connector command flow and a 30-day reminder cooldown per WordPress user.
                </div>
                {adminPortalFeatures.requiresConfirmation && adminMfaOutreachEnabled && (
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Confirm (type “ENABLE ADMIN MFA OUTREACH”)
                    </Label>
                    <Input
                      value={adminMfaOutreachConfirm}
                      onChange={(event) => setAdminMfaOutreachConfirm(event.target.value)}
                      placeholder="ENABLE ADMIN MFA OUTREACH"
                    />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={handleAdminMfaOutreachSettingSave}
                    disabled={savingAdminMfaOutreachSetting}
                  >
                    {savingAdminMfaOutreachSetting ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save setting'}
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    Visible only to super-admins.
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {testModeSettings && (
            <Card className="border-border/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Test Mode / Emulation</CardTitle>
                <CardDescription>Applies only to test users in the portal.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={testModeSettingEnabled}
                      onCheckedChange={(checked) => setTestModeSettingEnabled(checked)}
                      disabled={savingTestModeSetting}
                    />
                    <span className="text-sm text-muted-foreground">
                      Allow test mode emulation
                    </span>
                  </div>
                  <Badge variant={testModeSettingEnabled ? 'default' : 'secondary'}>
                    {testModeSettingEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  Test mode affects what the WordPress plugin enforces. Use only for internal testing.
                </div>
                {testModeSettings.requiresConfirmation && testModeSettingEnabled && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Confirm (type “ENABLE TEST MODE”)</Label>
                    <Input
                      value={testModeSettingConfirm}
                      onChange={(event) => setTestModeSettingConfirm(event.target.value)}
                      placeholder="ENABLE TEST MODE"
                    />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={handleTestModeSettingSave} disabled={savingTestModeSetting}>
                    {savingTestModeSetting ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save setting'}
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    Visible only to super-admins.
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Policy Sync Check (Portal ↔ Plugin)</CardTitle>
              <CardDescription>
                Admin-only verification. Compares portal policy update time with the latest plugin heartbeat and pending commands.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Site ID or Domain ID</Label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input
                    value={policySyncTarget}
                    onChange={(event) => setPolicySyncTarget(event.target.value)}
                    placeholder="UUID (site_id or domain_id)"
                  />
                  <Button size="sm" onClick={handlePolicySyncCheck} disabled={policySyncLoading}>
                    {policySyncLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Run check'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  This check ignores local-only fields (e.g. cached timestamps) and focuses on portal policy vs heartbeat timing.
                </p>
              </div>

              {policySyncResult && (
                <div className="space-y-3 rounded-md border border-border/50 bg-background/60 p-3 text-xs text-muted-foreground">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-medium text-foreground">Status</div>
                    <Badge variant={policySyncResult.inSync ? 'default' : 'destructive'}>
                      {policySyncResult.inSync ? 'In sync' : 'Out of sync'}
                    </Badge>
                  </div>
                  <div className="grid gap-2 md:grid-cols-2">
                    <div>
                      <div className="text-[11px] uppercase tracking-wide">Site ID</div>
                      <div className="font-mono text-[11px] text-foreground">{policySyncResult.siteId}</div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wide">Domain</div>
                      <div className="text-[11px] text-foreground">
                        {policySyncResult.domainName || policySyncResult.domainId || 'Unknown'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wide">Site URL</div>
                      <div className="text-[11px] text-foreground">{policySyncResult.siteUrl || 'Unknown'}</div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wide">Plugin version</div>
                      <div className="text-[11px] text-foreground">{policySyncResult.pluginVersion || 'Unknown'}</div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wide">Policy updated</div>
                      <div className="text-[11px] text-foreground">{formatTimestamp(policySyncResult.policyUpdatedAt)}</div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wide">Last heartbeat</div>
                      <div className="text-[11px] text-foreground">{formatTimestamp(policySyncResult.lastHeartbeatAt)}</div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wide">Policy tier</div>
                      <div className="text-[11px] text-foreground">{policySyncResult.policyTier || 'Unknown'}</div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wide">Policy locked</div>
                      <div className="text-[11px] text-foreground">
                        {policySyncResult.policyLocked === null ? 'Unknown' : policySyncResult.policyLocked ? 'Yes' : 'No'}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide">Pending commands</div>
                    <div className="text-[11px] text-foreground">{policySyncResult.pendingCommands.length}</div>
                  </div>
                  {!policySyncResult.inSync && policySyncResult.reasons.length > 0 && (
                    <div className="space-y-1">
                      <div className="text-[11px] uppercase tracking-wide">Reasons</div>
                      <ul className="list-disc space-y-1 pl-4 text-[11px] text-foreground">
                        {policySyncResult.reasons.map((reason) => (
                          <li key={reason}>{reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Maintenance Utilities</CardTitle>
              <CardDescription>Internal operations tools. Use with care.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Confirm (type “CONFIRM”)</Label>
                <Input
                  value={repairConfirm}
                  onChange={(event) => setRepairConfirm(event.target.value)}
                  placeholder="CONFIRM"
                />
                <Button
                  variant="outline"
                  onClick={handleRepairManagedSites}
                  disabled={repairingManagedSites || repairConfirm !== 'CONFIRM'}
                >
                  {repairingManagedSites ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Repair site links
                </Button>
                <p className="text-xs text-muted-foreground">
                  Repairs managed site links by matching site_url to domain_name.
                </p>
              </div>
            </CardContent>
          </Card>

          {unlinkedSites.length > 0 && (
            <Card className="border-border/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Unlinked managed sites</CardTitle>
                <CardDescription>Sites missing domain links. Repair if needed.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-2 text-xs text-muted-foreground md:grid-cols-2">
                {unlinkedSites.slice(0, 6).map((site) => (
                  <div key={site.id} className="rounded-md border border-border/50 bg-background/60 px-3 py-2">
                    <div className="font-mono text-[11px] text-foreground">{site.site_url}</div>
                    <div className="text-[11px]">User: {site.user_id}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <RoleManagementCard currentUserId={user?.id || ''} />
        </div>
      )}

      <Dialog open={!!selectedAccount} onOpenChange={(open) => !open && setSelectedAccount(null)}>
        <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              User details
            </DialogTitle>
            <DialogDescription>
              {selectedAccount?.email || 'Selected user'}
            </DialogDescription>
          </DialogHeader>
          {selectedAccount && (
            <div className="space-y-4">
              <Card className="border-border/60">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Account controls</CardTitle>
                  <CardDescription>Access and billing overrides for this user.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleImpersonate(selectedAccount)}
                    >
                      View customer dashboard
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span>Portal MFA</span>
                    <Badge variant={selectedAccount.mfaMethod ? 'default' : 'secondary'}>
                      {selectedAccount.mfaMethod ? 'Enabled' : 'Off'}
                    </Badge>
                    {selectedAccount.mfaMethod && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDisableMfa(selectedAccount)}
                      >
                        Disable MFA
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span>Payment bypass</span>
                    <Switch
                      checked={Boolean(selectedAccount.paymentLockBypass)}
                      onCheckedChange={(checked) => handlePaymentBypassToggle(selectedAccount, checked)}
                      disabled={updatingBypassUserId === selectedAccount.userId}
                    />
                  </div>
                </CardContent>
              </Card>

              {selectedAccount.portfolioPricingApplication && (
                <Card className="border-border/60">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Portfolio pricing application</CardTitle>
                    <CardDescription>
                      Review the submitted account details before approving a private pricing path.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={getPortfolioPricingStatusTone(selectedAccount.portfolioPricingApplication.status)}>
                        {PORTFOLIO_PRICING_STATUS_LABELS[selectedAccount.portfolioPricingApplication.status]}
                      </Badge>
                      {selectedAccount.portfolioPricingApplication.signupSource ? (
                        <Badge variant="outline">
                          Signup source {selectedAccount.portfolioPricingApplication.signupSource}
                        </Badge>
                      ) : null}
                      <Badge variant="outline">
                        Submitted via {selectedAccount.portfolioPricingApplication.submissionSource}
                      </Badge>
                      {selectedAccount.portfolioPricingApplication.historyCount > 1 ? (
                        <Badge variant="outline">
                          {selectedAccount.portfolioPricingApplication.historyCount} submissions
                        </Badge>
                      ) : null}
                      <Badge variant="outline">
                        {selectedAccount.portfolioPricingApplication.expectedSiteCount} planned site(s)
                      </Badge>
                    </div>

                    <div className="rounded-md border border-border/50 bg-muted/10 px-3 py-3 text-sm">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="mt-0.5 h-4 w-4 text-primary" />
                        <div className="space-y-2">
                          <p className="font-medium text-foreground">Review for fit before approving a private pricing path.</p>
                          <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
                            <li>Confirm the business context looks legitimate and relevant to the account.</li>
                            <li>Look for a clear multi-site, client, or portfolio use case with meaningful expected ShadowScan usage.</li>
                            <li>Prefer accounts that appear commercially coherent and operationally manageable to support.</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2 text-sm">
                      <div>
                        <div className="text-xs text-muted-foreground">Business</div>
                        <div>{selectedAccount.portfolioPricingApplication.businessName}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Website</div>
                        <div>{selectedAccount.portfolioPricingApplication.companyUrl || 'Not provided'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Role / account type</div>
                        <div>{selectedAccount.portfolioPricingApplication.roleAccountType}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Approximate portfolio size</div>
                        <div>{selectedAccount.portfolioPricingApplication.approximatePortfolioSize || 'Not provided'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Current managed sites</div>
                        <div>{selectedAccount.portfolioPricingApplication.currentSiteCount ?? 'Not provided'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Expected ShadowScan sites</div>
                        <div>{selectedAccount.portfolioPricingApplication.expectedSiteCount}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Site ownership</div>
                        <div>{selectedAccount.portfolioPricingApplication.siteOwnershipType}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Pricing need</div>
                        <div>{selectedAccount.portfolioPricingApplication.pricingNeed || 'Not provided'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Primary market</div>
                        <div>{selectedAccount.portfolioPricingApplication.primaryMarket || 'Not provided'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Expected onboarding timeframe</div>
                        <div>{selectedAccount.portfolioPricingApplication.expectedOnboardingTimeframe || 'Not provided'}</div>
                      </div>
                      <div className="md:col-span-2">
                        <div className="text-xs text-muted-foreground">Use case</div>
                        <div className="rounded-md border border-border/50 bg-muted/10 px-3 py-2 leading-relaxed">
                          {selectedAccount.portfolioPricingApplication.useCaseSummary}
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="portfolio-review-notes">Review notes</Label>
                        <Textarea
                          id="portfolio-review-notes"
                          value={portfolioReviewNotes}
                          onChange={(event) => setPortfolioReviewNotes(event.target.value)}
                          placeholder="Optional note visible in the account record."
                          className="min-h-[100px]"
                        />
                      </div>
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label htmlFor="portfolio-approval-price">Approved private price</Label>
                          <select
                            id="portfolio-approval-price"
                            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                            value={portfolioApprovalPriceCents}
                            onChange={(event) => setPortfolioApprovalPriceCents(Number(event.target.value))}
                          >
                            {(inviteSettings?.priceCatalog || [])
                              .filter((row) => row.active)
                              .map((row) => (
                                <option key={row.priceCents} value={row.priceCents}>
                                  AUD ${(row.priceCents / 100).toFixed(2)} / domain / month
                                </option>
                              ))}
                          </select>
                          <p className="text-xs text-muted-foreground">
                            Approval assigns the existing private dev-account pricing path. It is not applied automatically until you confirm approval here.
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="portfolio-approval-min-active-domains">Minimum active paid domains to retain reviewed pricing</Label>
                          <Input
                            id="portfolio-approval-min-active-domains"
                            type="number"
                            min={1}
                            step={1}
                            value={portfolioApprovalMinimumActiveDomains}
                            onChange={(event) => setPortfolioApprovalMinimumActiveDomains(Math.max(1, Number(event.target.value) || 1))}
                          />
                          <p className="text-xs text-muted-foreground">
                            If active paid reviewed-pricing domains remain below this threshold for one full billing cycle, the account is flagged for manual admin reassessment only.
                          </p>
                        </div>
                        <div className="space-y-2 text-xs text-muted-foreground">
                          <div>Submitted {new Date(selectedAccount.portfolioPricingApplication.submittedAt).toLocaleString()}</div>
                          {selectedAccount.portfolioPricingApplication.reviewedAt ? (
                            <div>Last reviewed {new Date(selectedAccount.portfolioPricingApplication.reviewedAt).toLocaleString()}</div>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    {selectedAccount.portfolioPricingApplication.status === 'pending_review' ||
                    selectedAccount.portfolioPricingApplication.status === 'under_review' ? (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePortfolioPricingReview(selectedAccount, 'under_review')}
                          disabled={reviewingPortfolioApplicationId === selectedAccount.portfolioPricingApplication.id}
                        >
                          Mark under review
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handlePortfolioPricingReview(selectedAccount, 'approved')}
                          disabled={
                            reviewingPortfolioApplicationId === selectedAccount.portfolioPricingApplication.id ||
                            !(inviteSettings?.priceCatalog || []).some((row) => row.active) ||
                            portfolioApprovalMinimumActiveDomains < 1
                          }
                        >
                          Approve and assign private pricing
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePortfolioPricingReview(selectedAccount, 'declined')}
                          disabled={reviewingPortfolioApplicationId === selectedAccount.portfolioPricingApplication.id}
                        >
                          Decline
                        </Button>
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              )}

              {(selectedAccount.accountKind === 'dev' || typeof selectedAccount.reviewedPricingMinActiveDomains === 'number') && (
                <Card className="border-border/60">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Reviewed pricing retention</CardTitle>
                    <CardDescription>
                      Internal guidance only. Falling below the agreed threshold triggers manual review, not automatic repricing.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="grid gap-3 md:grid-cols-2">
                      <div>
                        <div className="text-xs text-muted-foreground">Minimum active paid domains</div>
                        <div>{selectedAccount.reviewedPricingMinActiveDomains ?? 'Not configured'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Current active paid reviewed-pricing domains</div>
                        <div>{selectedAccount.activePaidReviewedPricingDomains ?? 0}</div>
                      </div>
                    </div>
                    {selectedAccount.reviewedPricingReassessmentNeeded ? (
                      <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="destructive">Manual reassessment required</Badge>
                          {selectedAccount.reviewedPricingReassessmentFlaggedAt ? (
                            <span className="text-xs text-muted-foreground">
                              Flagged {new Date(selectedAccount.reviewedPricingReassessmentFlaggedAt).toLocaleString()}
                            </span>
                          ) : null}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {selectedAccount.reviewedPricingReassessmentReason ||
                            'This account stayed below its agreed reviewed-pricing threshold for one full billing cycle. Review before making any future repricing decision.'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleClearReviewedPricingReassessment(selectedAccount)}
                            disabled={clearingReviewedPricingReassessmentUserId === selectedAccount.userId}
                          >
                            Mark reassessment reviewed
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-muted-foreground">
                        No manual reassessment is currently required.
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {!selectedAccount.portfolioPricingApplication && (
                <Card className="border-border/60">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Portfolio pricing application</CardTitle>
                    <CardDescription>
                      No portfolio pricing application has been submitted for this account.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground">
                    If this customer should be reviewed for portfolio pricing, they can apply during signup or later from Billing &amp; subscriptions.
                  </CardContent>
                </Card>
              )}

              {isSuperAdmin && (
                <Card className="border-border/60">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Test mode (per user)</CardTitle>
                    <CardDescription>Visible only to super-admins.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {!testModeSettings?.allowed && (
                      <div className="text-xs text-muted-foreground">
                        Test mode emulation is disabled in this environment.
                      </div>
                    )}
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={getTestModeInput(selectedAccount).isTestUser}
                          onCheckedChange={(checked) => updateTestModeInput(selectedAccount, { isTestUser: checked })}
                          disabled={!testModeSettings?.allowed || updatingTestModeUserId === selectedAccount.userId}
                        />
                        <span className="text-sm text-muted-foreground">Mark as Test User</span>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Emulate subscription status</label>
                        <select
                          className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
                          value={getTestModeInput(selectedAccount).emulationStatus}
                          onChange={(event) => updateTestModeInput(selectedAccount, { emulationStatus: event.target.value })}
                          disabled={!testModeSettings?.allowed || updatingTestModeUserId === selectedAccount.userId}
                        >
                          <option value="none">None (real)</option>
                          <option value="trial">TRIAL</option>
                          <option value="active">ACTIVE</option>
                          <option value="past_due">PAST_DUE</option>
                          <option value="suspended">SUSPENDED</option>
                          <option value="cancelled">CANCELLED</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Grace end (PAST_DUE)</label>
                        <input
                          className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
                          placeholder="YYYY-MM-DD HH:MM"
                          value={getTestModeInput(selectedAccount).graceEnd}
                          onChange={(event) => updateTestModeInput(selectedAccount, { graceEnd: event.target.value })}
                          disabled={!testModeSettings?.allowed || updatingTestModeUserId === selectedAccount.userId}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Suspended since (SUSPENDED)</label>
                        <input
                          className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
                          placeholder="YYYY-MM-DD HH:MM"
                          value={getTestModeInput(selectedAccount).suspendedSince}
                          onChange={(event) => updateTestModeInput(selectedAccount, { suspendedSince: event.target.value })}
                          disabled={!testModeSettings?.allowed || updatingTestModeUserId === selectedAccount.userId}
                        />
                      </div>
                      {testModeSettings?.requiresConfirmation && getTestModeInput(selectedAccount).isTestUser && (
                        <div className="md:col-span-2">
                          <label className="text-xs text-muted-foreground">Confirm (type “ENABLE TEST MODE”)</label>
                          <input
                            className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
                            placeholder="ENABLE TEST MODE"
                            value={getTestModeInput(selectedAccount).confirmPhrase}
                            onChange={(event) => updateTestModeInput(selectedAccount, { confirmPhrase: event.target.value })}
                            disabled={!testModeSettings?.allowed || updatingTestModeUserId === selectedAccount.userId}
                          />
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleTestModeUpdate(selectedAccount)}
                      disabled={!testModeSettings?.allowed || updatingTestModeUserId === selectedAccount.userId}
                    >
                      {updatingTestModeUserId === selectedAccount.userId ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        'Save test mode'
                      )}
                    </Button>
                  </CardContent>
                </Card>
              )}

              <Card className="border-border/60">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Sites</CardTitle>
                  <CardDescription>Domains linked to this account.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {selectedAccount.domains.length === 0 ? (
                    <p className="text-muted-foreground">No sites linked.</p>
                  ) : (
                    selectedAccount.domains.map((domain) => (
                      <div
                        key={domain.id}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/50 bg-muted/20 px-3 py-2"
                      >
                        <span className="font-mono">{domain.domainName}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDiagnosticsDomain({
                            domain,
                            paymentBypass: Boolean(selectedAccount.paymentLockBypass),
                            managedSite:
                              managedSiteByDomainId.byDomainId.get(domain.id) ||
                              managedSiteByDomainId.bySiteUrl.get(normalizeDomainValue(domain.domainName) || ''),
                          })}
                        >
                          Diagnostics
                        </Button>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              <Card className="border-border/60">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Audit trail</CardTitle>
                  <CardDescription>Recent admin actions for this user.</CardDescription>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">
                  No recent admin actions recorded.
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedIncident} onOpenChange={(open) => !open && setSelectedIncident(null)}>
        <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              Incident detail
            </DialogTitle>
            <DialogDescription>
              Review context, add notes, and update status safely.
            </DialogDescription>
          </DialogHeader>

          {!selectedIncident || incidentDetailLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading incident...
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid gap-3 md:grid-cols-2">
                <Card className="border-border/60">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Incident</CardTitle>
                    <CardDescription>ID {selectedIncident.id}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div>
                      <span className="text-xs text-muted-foreground">Status</span>
                      <div>{selectedIncident.status.replace('_', ' ')}</div>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Severity</span>
                      <div>{selectedIncident.severity}</div>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Domain</span>
                      <div className="font-mono">{selectedIncident.domain_name || 'Unknown'}</div>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Signal</span>
                      <div>{selectedIncident.signal_summary || selectedIncident.signal_type}</div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-border/60">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Ownership</CardTitle>
                    <CardDescription>Assignment and resolution state.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div>
                      <span className="text-xs text-muted-foreground">Assigned to</span>
                      <div>{selectedIncident.assigned_to || 'Unassigned'}</div>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Acknowledged by</span>
                      <div>{selectedIncident.acknowledged_by || 'Not acknowledged'}</div>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Resolved by</span>
                      <div>{selectedIncident.resolved_by || 'Not resolved'}</div>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Resolution reason</span>
                      <div>{selectedIncident.resolution_reason || '-'}</div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card className="border-border/60">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Actions</CardTitle>
                  <CardDescription>Update or resolve with concurrency protection.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-3 md:grid-cols-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Assign to</Label>
                      <Input
                        value={incidentAssignTo}
                        onChange={(event) => setIncidentAssignTo(event.target.value)}
                        placeholder="admin@shadowscan.com.au"
                      />
                      <Button
                        size="sm"
                        className="mt-2"
                        onClick={handleIncidentAssign}
                        disabled={incidentActionLoading || !incidentAssignTo}
                      >
                        Assign
                      </Button>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Update severity</Label>
                      <select
                        className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
                        value={incidentUpdateSeverity}
                        onChange={(event) => setIncidentUpdateSeverity(event.target.value)}
                      >
                        <option value="">No change</option>
                        <option value="SEV1">SEV1</option>
                        <option value="SEV2">SEV2</option>
                        <option value="SEV3">SEV3</option>
                      </select>
                      <Label className="mt-2 text-xs text-muted-foreground">Update signal summary</Label>
                      <Input
                        value={incidentUpdateSignal}
                        onChange={(event) => setIncidentUpdateSignal(event.target.value)}
                        placeholder="Short summary"
                      />
                      <Button
                        size="sm"
                        className="mt-2"
                        onClick={handleIncidentUpdate}
                        disabled={incidentActionLoading || (!incidentUpdateSeverity && !incidentUpdateSignal)}
                      >
                        Save updates
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleIncidentAck}
                        disabled={incidentActionLoading}
                      >
                        Acknowledge
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleIncidentReopen}
                        disabled={incidentActionLoading}
                      >
                        Reopen
                      </Button>
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Add note</Label>
                      <Input
                        value={incidentNote}
                        onChange={(event) => setIncidentNote(event.target.value)}
                        placeholder="What did you check?"
                      />
                      <Button
                        size="sm"
                        className="mt-2"
                        onClick={handleIncidentNote}
                        disabled={incidentActionLoading || !incidentNote.trim()}
                      >
                        Add note
                      </Button>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Resolve</Label>
                      <select
                        className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
                        value={incidentResolveType}
                        onChange={(event) => setIncidentResolveType(event.target.value as 'RESOLVED' | 'FALSE_POSITIVE')}
                      >
                        <option value="RESOLVED">Resolved</option>
                        <option value="FALSE_POSITIVE">False positive</option>
                      </select>
                      <Input
                        className="mt-2"
                        value={incidentResolveReason}
                        onChange={(event) => setIncidentResolveReason(event.target.value)}
                        placeholder="Resolution reason"
                      />
                      <Button
                        size="sm"
                        className="mt-2"
                        onClick={handleIncidentResolve}
                        disabled={incidentActionLoading || !incidentResolveReason.trim()}
                      >
                        Resolve
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/60">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Timeline</CardTitle>
                  <CardDescription>Latest events first.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {incidentEvents.length === 0 ? (
                    <div className="text-muted-foreground">No events recorded.</div>
                  ) : (
                    incidentEvents.map((event) => (
                      <div key={event.id} className="rounded-md border border-border/60 bg-muted/10 p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="text-xs text-muted-foreground">{event.event_type}</div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(event.created_at).toLocaleString()}
                          </div>
                        </div>
                        <div className="mt-1">{event.message || '-'}</div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {event.actor_id ? `by ${event.actor_id}` : 'system'}
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!diagnosticsDomain} onOpenChange={(open) => !open && setDiagnosticsDomain(null)}>
        <DialogContent className="sm:max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              Diagnostics
            </DialogTitle>
            <DialogDescription>
              {diagnosticsDomain?.domain.domainName || 'Selected domain'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {diagnosticsDomain && (
              <Card className="border-border/60">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Admin Controls</CardTitle>
                  <CardDescription>Manage connector state and billing overrides.</CardDescription>
                </CardHeader>
                <CardContent>
                  {renderAdminControls(
                    diagnosticsDomain.domain,
                    diagnosticsDomain.managedSite || diagnosticsSite,
                    diagnosticsDomain.paymentBypass
                  )}
                </CardContent>
              </Card>
            )}

            <Card className="border-border/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Managed Site Snapshot</CardTitle>
                <CardDescription>Heartbeat and connector state.</CardDescription>
              </CardHeader>
              <CardContent className="text-sm grid gap-2 md:grid-cols-2">
                <div>
                  <span className="text-xs text-muted-foreground">Last heartbeat</span>
                  <div className="font-mono">
                    {diagnosticsSite?.last_heartbeat_at
                      ? new Date(diagnosticsSite.last_heartbeat_at).toLocaleString()
                      : diagnosticsDomain?.managedSite?.last_heartbeat_at
                        ? new Date(diagnosticsDomain.managedSite.last_heartbeat_at).toLocaleString()
                        : 'Never'}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">WP Admin URL</span>
                  <div className="text-xs text-muted-foreground">
                    {diagnosticsSite?.wp_admin_url || diagnosticsDomain?.managedSite?.wp_admin_url || 'Not reported'}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Site URL</span>
                  <div className="text-xs text-muted-foreground">
                    {diagnosticsSite?.site_url || diagnosticsDomain?.managedSite?.site_url || 'Unknown'}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Hosted automation</span>
                  <div className="text-xs text-muted-foreground">
                    {resolveHostedAutomation(diagnosticsSite || diagnosticsDomain?.managedSite).label}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">WHMCS service</span>
                  <div className="text-xs text-muted-foreground">
                    {(diagnosticsSite?.hosted_whmcs_service_id || diagnosticsDomain?.managedSite?.hosted_whmcs_service_id)
                      ? `#${diagnosticsSite?.hosted_whmcs_service_id || diagnosticsDomain?.managedSite?.hosted_whmcs_service_id}`
                      : 'Not linked'}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Service Coverage</CardTitle>
                <CardDescription>Key protection and monitoring signals from the connector.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-2 text-xs">
                <div>
                  <span className="text-xs text-muted-foreground">Guard profile</span>
                  <div className="mt-1">{renderReportValue(diagnosticsReport['Guard profile'])}</div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Login abuse safe mode</span>
                  <div className="mt-1">{renderReportValue(diagnosticsReport['Login abuse safe mode'])}</div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Emergency mode</span>
                  <div className="mt-1">{renderReportValue(diagnosticsReport['Emergency mode'])}</div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">MFA enforcement</span>
                  <div className="mt-1">{renderReportValue(diagnosticsReport['MFA enforcement'])}</div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Enumeration strict</span>
                  <div className="mt-1">{renderReportValue(diagnosticsReport['Enumeration strict'])}</div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">X-Frame-Options</span>
                  <div className="mt-1">{renderReportValue(diagnosticsReport['X-Frame-Options'])}</div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">CSP report-only</span>
                  <div className="mt-1">{renderReportValue(diagnosticsReport['CSP report-only'])}</div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Safe mode</span>
                  <div className="mt-1">{renderReportValue(diagnosticsReport['Safe mode'])}</div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Last integrity scan</span>
                  <div className="mt-1">{renderReportValue(diagnosticsReport['Last integrity scan'])}</div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Last auth anomaly</span>
                  <div className="mt-1">{renderReportValue(diagnosticsReport['Last auth anomaly'])}</div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Events queued</span>
                  <div className="mt-1">{renderReportValue(diagnosticsReport['Events queued'])}</div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Remote diagnostics</span>
                  <div className="mt-1">{renderReportValue(diagnosticsReport['Remote diagnostics'])}</div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Can install plugins</span>
                  <div className="mt-1">{renderReportValue(diagnosticsReport['Can install plugins'])}</div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Can activate plugins</span>
                  <div className="mt-1">{renderReportValue(diagnosticsReport['Can activate plugins'])}</div>
                </div>
              </CardContent>
            </Card>

            {diagnosticsDomain ? (
              <AdminMfaOutreachCard
                domainId={diagnosticsDomain.domain.id}
                enabled={Boolean(adminPortalFeatures?.adminMfaOutreachEnabled)}
              />
            ) : null}

            <Card className="border-border/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Connector Report</CardTitle>
                <CardDescription>Latest report from the WordPress connector.</CardDescription>
              </CardHeader>
              <CardContent className="text-xs">
                {diagnosticsSite?.connector_report ? (
                  <pre className="whitespace-pre-wrap font-mono rounded-md border border-border/50 bg-muted/40 px-3 py-2">
                    {diagnosticsSite.connector_report}
                  </pre>
                ) : (
                  <p className="text-muted-foreground">No connector report available yet.</p>
                )}
              </CardContent>
            </Card>

            <Card className="border-border/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Remote Diagnostics</CardTitle>
                <CardDescription>Trigger diagnostic commands and log collection.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-xs">
                <p className="text-muted-foreground">
                  {diagnosticsSite?.connector_report?.includes('Remote diagnostics: Enabled')
                    ? 'Remote diagnostics enabled on site.'
                    : 'Remote diagnostics may be disabled on site.'}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => handleSelfCheck(diagnosticsTargetSite)}>
                    Refresh status
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => handleDebugFetchLogs(diagnosticsTargetSite)}>
                    Fetch logs
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDebugInstallMu(diagnosticsTargetSite)}>
                    Install MU helper
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDebugRemoveMu(diagnosticsTargetSite)}>
                    Remove MU helper
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Heartbeat Activity</CardTitle>
                <CardDescription>Recent connector events and wp-admin URL changes.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-xs">
                {diagnosticsQuery.isFetching ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading diagnostics...
                  </div>
                ) : diagnosticsLogs.length === 0 ? (
                  <p className="text-muted-foreground">No recent heartbeat events.</p>
                ) : (
                  <>
                    {diagnosticsLogsSlice.map((log: any, idx: number) => (
                      <div
                        key={`${log.accessed_at}-${log.action}-${idx}`}
                        className="rounded-md border border-border/50 bg-muted/40 px-3 py-2"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline">{String(log.action).replace(/_/g, ' ')}</Badge>
                          <span className="text-muted-foreground">
                            {new Date(log.accessed_at).toLocaleString()}
                          </span>
                        </div>
                        {log.metadata?.wpAdminUrl && (
                          <div className="text-muted-foreground">URL: {log.metadata.wpAdminUrl}</div>
                        )}
                        {log.metadata?.candidateWpAdminUrl && (
                          <div className="text-muted-foreground">Candidate: {log.metadata.candidateWpAdminUrl}</div>
                        )}
                        {log.metadata?.reportedSiteUrl && (
                          <div className="text-muted-foreground">Mismatch: {log.metadata.reportedSiteUrl}</div>
                        )}
                      </div>
                    ))}
                    <div className="flex items-center justify-between pt-2 text-xs text-muted-foreground">
                      <span>
                        Showing {diagnosticsLogsSliceStart + 1}
                        {'-'}
                        {Math.min(diagnosticsLogsSliceStart + diagnosticsLogsPerPage, diagnosticsLogs.length)}
                        {' '}of {diagnosticsLogs.length}
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDiagnosticsLogPage((prev) => Math.max(1, prev - 1))}
                          disabled={diagnosticsLogPageSafe === 1}
                        >
                          Previous
                        </Button>
                        <span>
                          Page {diagnosticsLogPageSafe} of {diagnosticsLogPages}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDiagnosticsLogPage((prev) => Math.min(diagnosticsLogPages, prev + 1))}
                          disabled={diagnosticsLogPageSafe === diagnosticsLogPages}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card className="border-border/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Command Results</CardTitle>
                <CardDescription>Latest diagnostics command outcomes.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-xs">
                {diagnosticsCommands.length === 0 ? (
                  <p className="text-muted-foreground">No command results yet.</p>
                ) : (
                  diagnosticsCommands.map((command: any) => {
                    const payload = command.payload || {};
                    const isMuCommand = command.type === 'debug_install_mu_helper' || command.type === 'debug_remove_mu_helper';
                    const muInstalled = typeof payload.installed === 'boolean' ? payload.installed : null;
                    const muPath = typeof payload.path === 'string' ? payload.path : null;
                    const muWasPresent = typeof payload.was_present === 'boolean' ? payload.was_present : null;
                    return (
                      <div
                        key={command.id}
                        className="rounded-md border border-border/50 bg-muted/40 px-3 py-2"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <Badge variant="outline">{String(command.type).replace(/_/g, ' ')}</Badge>
                          <span className="text-muted-foreground">
                            {command.completed_at
                              ? new Date(command.completed_at).toLocaleString()
                              : 'Pending'}
                          </span>
                        </div>
                        {command.error && (
                          <div className="mt-2 text-[hsl(var(--ss-warning))]">Error: {command.error}</div>
                        )}
                        {isMuCommand && (
                          <div className="mt-2 space-y-1 text-muted-foreground">
                            {muInstalled !== null && (
                              <div>Installed: {muInstalled ? 'Yes' : 'No'}</div>
                            )}
                            {muWasPresent !== null && (
                              <div>Previously present: {muWasPresent ? 'Yes' : 'No'}</div>
                            )}
                            {muPath && <div>Path: {muPath}</div>}
                          </div>
                        )}
                        {payload?.debug_log_tail && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => saveTextFile('debug.log', payload.debug_log_tail)}
                            >
                              Download debug.log tail
                            </Button>
                            {payload.php_error_log_tail && (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => saveTextFile('error_log', payload.php_error_log_tail)}
                              >
                                Download error_log tail
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={inviteDialogOpen}
        onOpenChange={(open) => {
          setInviteDialogOpen(open);
          if (!open) resetInviteForm();
        }}
      >
        <DialogContent className="max-w-lg border-border/50">
          <DialogHeader>
            <DialogTitle>Invite Account</DialogTitle>
            <DialogDescription>
              Invited users must complete password setup, service agreement acceptance, and MFA before portal access.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invite-account-kind">Account type</Label>
              <select
                id="invite-account-kind"
                value={inviteAccountKind}
                onChange={(e) => setInviteAccountKind(e.target.value === 'dev' ? 'dev' : 'standard')}
                className="w-full rounded-md border border-border/60 bg-background px-3 py-2 text-sm"
              >
                <option value="standard">Standard</option>
                <option value="dev">Dev</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="invite-full-name">Full name</Label>
              <Input
                id="invite-full-name"
                value={inviteFullName}
                onChange={(e) => setInviteFullName(e.target.value)}
                placeholder="Jane Doe"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </div>

            {inviteAccountKind === 'dev' && (
              <div className="space-y-2">
                <Label htmlFor="invite-dev-price">Default dev plan price (AUD)</Label>
                <select
                  id="invite-dev-price"
                  value={String(inviteDevPriceCents)}
                  onChange={(e) => setInviteDevPriceCents(Number(e.target.value))}
                  className="w-full rounded-md border border-border/60 bg-background px-3 py-2 text-sm"
                >
                  {(inviteSettings?.priceCatalog || [])
                    .filter((row) => row.active)
                    .map((row) => (
                      <option key={`${row.currency}-${row.priceCents}-${row.stripePriceId}`} value={String(row.priceCents)}>
                        {row.currency} ${(row.priceCents / 100).toFixed(2)}
                      </option>
                    ))}
                </select>
              </div>
            )}

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setInviteDialogOpen(false)} disabled={invitingAccount}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={() => void handleInviteAccount()} disabled={invitingAccount}>
                {invitingAccount ? 'Sending…' : 'Send Invite'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {isAdmin === true && (
        <AdminAiAssistantWidget
          enabled={Boolean(adminPortalFeatures?.adminAiAssistantEnabled)}
          rolloutKnown={Boolean(adminPortalFeatures)}
          contextDomainId={diagnosticsDomain?.domain.id ?? null}
          contextAccountId={selectedAccount?.userId ?? null}
        />
      )}
    </div>
  );

  if (embedded) {
    return (
      <>
        {panelContent}
      </>
    );
  }

  const showAdminHomeButton = location.pathname !== '/portal/admin';

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Helmet>
        <title>Admin Console | ShadowScan Portal</title>
        <meta name="robots" content="noindex,nofollow" />
        <meta
          name="description"
          content="Internal admin view of ShadowScan portal users and domains."
        />
      </Helmet>
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />

      <AuthenticatedHeader
        userEmail={user?.email || ''}
        context="admin"
        roleLabel={isSuperAdmin ? 'Super Admin' : 'Admin'}
        showLiveScan={true}
        showPromotions={true}
        leftSlot={(
          <div className="flex items-center gap-3">
            <LogoShield className="h-7 w-7" />
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-mono text-muted-foreground">ShadowScan Portal</span>
              <span className="text-xl font-heading font-bold tracking-tight">Admin Console</span>
            </div>
            {testModeSettings?.enabled && (
              <Badge variant="warning">
                Test Mode Enabled
              </Badge>
            )}
          </div>
        )}
        centerSlot={showAdminHomeButton ? (
          <Button variant="ghost" size="sm" onClick={() => navigate('/portal/admin')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Admin console home
          </Button>
        ) : undefined}
      />

      <main className="relative z-10 container mx-auto px-4 py-8">
        {panelContent}
      </main>
      <CurrencyDock />
    </div>
  );
}
